import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './SalesDashboardNew.css';
import { getDashboardOverview, getDashboardSiteVisits, getDashboardLeadsCalls, getDashboardTokens, getDashboardAllotments, getDashboardAgreements, getDashboardPerformance, getDashboardPersona, getDashboardPersonaDrilldown, getDashboardProjects } from '../../Services/SalesDashboardNew';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

/*
  Sales Dashboard — real-data build.

  Overview and Site Visits are wired to real backend endpoints
  (controllers/salesDashboard/overviewController.js and
  siteVisitsController.js). Every other tab (Performance, Leads & Calls,
  Persona, Tokens, Allotments, Agreements, Projects) has no backend endpoint
  yet, so it renders an honest "not connected yet" placeholder instead of
  fabricated numbers.

  It intentionally renders only the dashboard CONTENT area (header, identity
  band, tab strip, tab panels) — not a left sidebar. The app's real sidebar
  is rendered elsewhere already, same as the old SalesDashboard.jsx.
*/

// ---------------------------------------------------------------------------
// Constants & pure helpers
// ---------------------------------------------------------------------------

const L = 'https://cdn.jsdelivr.net/npm/lucide-static@0.469.0/icons/';
const ICON = (n) => L + n + '.svg';

const int = (n) => Math.round(n).toLocaleString('en-IN');
const cr = (n) => '₹ ' + n.toFixed(2) + ' Cr';
const sqft = (n) => int(n) + ' sqft';
const units = (n) => int(n) + (Math.round(n) === 1 ? ' unit' : ' units');
const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// kept for fidelity with the source design (it drives the sidebar nav list,
// which this component intentionally doesn't render — see the file header
// comment); not used below, so disabled rather than deleted.
// eslint-disable-next-line no-unused-vars
const NAV = [
  ['Event', 'calendar'], ['Project', 'layers'], ['Hold/Release', 'circle-pause'],
  ['SmartFlo', 'phone-call'], ['CallLogs', 'phone'], ['CpSourcing', 'phone-forwarded'],
  ['Sustenance', 'panel-left'], ['Site Visit Approval', 'calendar-check'], ['Token', 'ticket'],
  ['Leads', 'database'], ['FastBooking', 'zap'], ['All Allotments', 'list'],
].map(([label, ic]) => ({ label, icon: ICON(ic) }));

const TABS = ['Overview', 'Performance', 'Leads & Calls', 'Persona', 'Site Visits', 'Tokens', 'Allotments', 'Agreements', 'Projects'];

// Design-system status colors — matches the design's positive/warning/
// negative palette (#16A34A / #D97706 / #DC2626), same colors already used
// on the stat cards. Real AllotmentPayment.status values only; anything
// unrecognised falls back to the default blue rather than guessing.
// Rotating categorical palette for bars-list panels that break something
// into distinct TYPES (a status breakdown, four token buckets, call types,
// lead temperature) — matches the source design exactly: each row gets the
// next color in sequence, not a good/bad judgment. Panels that show a
// SEQUENCE instead (a funnel, "how far the leads got", calls by hour) stay
// one uniform color, same as the source design does for those.
const CAT = ['#2563EB', '#EC4899', '#06B6D4', '#F97316', '#16A34A', '#8B5CF6'];

// ---------------------------------------------------------------------------
// Real period -> {startMs, endMs} (epoch ms, IST calendar)
// ---------------------------------------------------------------------------

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000; // matches the backend's own IST helper

function istDayStart(ms) {
  const ist = ms + IST_OFFSET_MS;
  return Math.floor(ist / 86400000) * 86400000 - IST_OFFSET_MS;
}

function periodToRange(period, fromDateStr, toDateStr) {
  const todayStart = istDayStart(Date.now());
  const todayEnd = todayStart + 86400000 - 1;
  if (period === 'Today') return { startMs: todayStart, endMs: todayEnd };
  if (period === 'This Week') {
    const dow = new Date(todayStart + IST_OFFSET_MS + 43200000).getUTCDay(); // noon avoids boundary drift
    const sinceMonday = (dow + 6) % 7; // Monday = 0
    return { startMs: todayStart - sinceMonday * 86400000, endMs: todayEnd };
  }
  if (period === 'This Month') {
    const d = new Date(todayStart + IST_OFFSET_MS + 43200000);
    return { startMs: Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) - IST_OFFSET_MS, endMs: todayEnd };
  }
  if (period === 'Last Month') {
    const d = new Date(todayStart + IST_OFFSET_MS + 43200000);
    const thisMonthStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) - IST_OFFSET_MS;
    return { startMs: Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1) - IST_OFFSET_MS, endMs: thisMonthStart - 1 };
  }
  // Custom
  return {
    startMs: new Date(fromDateStr + 'T00:00:00+05:30').getTime(),
    endMs: new Date(toDateStr + 'T23:59:59+05:30').getTime(),
  };
}

function fmtShort(ms) {
  return new Date(ms).toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' });
}
function fmtYear(ms) {
  return new Date(ms).toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric' });
}
function dowOf(dateStr) {
  return new Date(dateStr + 'T12:00:00Z').getUTCDay(); // noon UTC — safe from any offset boundary drift
}
function nowIstParts() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }),
    time: now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }),
  };
}

// "+9%" style deltas from a real backend-computed percentage, or a plain
// dash when there's nothing to compare against (e.g. zero in the prior
// period) — never a fabricated number.
function fmtDelta(pct) {
  if (!pct) return { text: '—', color: '#6B7280' };
  return { text: (pct > 0 ? '▲ ' : '▼ ') + Math.abs(pct) + '% vs same days last month', color: pct > 0 ? '#16A34A' : '#DC2626' };
}

function formatLastActive(ms) {
  if (ms == null) return 'no activity yet';
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days <= 0) return 'active today';
  if (days === 1) return 'last active 1 day ago';
  return 'last active ' + days + ' days ago';
}

// A short, honest read of the real daily-activity series — busiest weekday
// and how many of the days in range had any logged activity at all.
function summarizeDailyActivity(dailyActivity) {
  if (!dailyActivity || !dailyActivity.length) return '';
  const totalDays = dailyActivity.length;
  const activeDays = dailyActivity.filter((d) => d.calls || d.visits || d.bookings).length;
  const busiest = dailyActivity.reduce((best, d) => ((d.calls + d.visits + d.bookings) > (best.calls + best.visits + best.bookings) ? d : best), dailyActivity[0]);
  const busiestLabel = new Date(busiest.date + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
  return 'Busiest on ' + busiestLabel + 's. Activity logged on ' + activeDays + ' of ' + totalDays + ' days this period.';
}

// The metrics object every band on this page reads from. Identity comes
// from the logged-in Redux user the instant it's known — no fetch needed,
// and no fallback to any sample roster. Every numeric field starts at zero
// and is overlaid with the real /salesDashboard/overview response once it
// loads (see the overlay below, in the component body).
function baseMetrics() {
  return {
    name: '', role: '', initials: '',
    leads: 0, leadsDeltaPct: 0,
    visits: 0, visitsDeltaPct: 0,
    tokens: 0, tokensDeltaPct: 0, viaToken: 0, direct: 0,
    allot: 0, allotDeltaPct: 0,
    agmt: 0, agmtDeltaPct: 0,
    valueCr: 0, valueCrDeltaPct: 0, area: 0, cancels: 0, cancelValueCr: 0,
    pending: 0, ready: 0, underSanction: 0,
    calls: 0, callsDeltaPct: 0, answered: 0, talkTimeSec: 0,
    projectsCount: 0, projectChips: [], lastActiveMs: null,
  };
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Icon({ name, size = 16, color = '#6B7280', style }) {
  const url = ICON(name);
  const mask = `url(${url}) center/contain no-repeat`;
  return (
    <div
      style={{
        width: size, height: size, flex: `0 0 ${size}px`, background: color,
        WebkitMask: mask, mask, ...style,
      }}
    />
  );
}

function InfoToggle({ open, onClick, size = 28, iconSize = 16, title = 'Show the note behind these numbers' }) {
  return (
    <div
      onClick={onClick}
      title={title}
      className="sdn-hover-info"
      style={{
        width: size, height: size, flex: `0 0 ${size}px`, borderRadius: 999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        background: open ? '#EFF4FF' : '#F7F8FA', border: `1px solid ${open ? '#BFDBFE' : '#EDEFF3'}`,
      }}
    >
      <Icon name="info" size={iconSize} color={open ? '#2563EB' : '#9CA3AF'} />
    </div>
  );
}

function CardShell({ title, sub, children }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: 20 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</div>
        <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 4, minHeight: 16 }}>{sub}</div>
      </div>
      {children}
    </div>
  );
}

function StatsGrid({ cols, stats, onStatClick, phone }) {
  return (
    <div style={{ display: 'grid', gap: phone ? 8 : 12, marginTop: phone ? 12 : 16, gridTemplateColumns: cols }}>
      {stats.map((s, i) => {
        // Phone-only: a thin colored left accent + a faint tint of that same
        // color (cycling the same CAT palette used for categorical bars
        // elsewhere), instead of every card being identical flat gray — plus
        // tighter padding/type so this first block doesn't dominate the
        // screen before any tab content is visible. Desktop is untouched.
        const accent = CAT[i % CAT.length];
        return (
          <div key={i} onClick={s.dimension && onStatClick ? () => onStatClick(s) : undefined}
            className={s.dimension ? 'sdn-hover-info' : undefined}
            style={phone
              ? { borderRadius: 10, border: `1px solid ${accent}2E`, borderLeft: `3px solid ${accent}`, background: `${accent}0D`, padding: '9px 10px', cursor: s.dimension ? 'pointer' : 'default' }
              : { borderRadius: 12, border: '1px solid #EDEFF3', background: '#F7F8FA', padding: 14, cursor: s.dimension ? 'pointer' : 'default' }}>
            <div style={{ fontSize: phone ? 8.5 : 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: phone ? accent : '#6B7280' }}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: phone ? 3 : 6, flexWrap: 'wrap' }}>
              <div style={{ fontSize: phone ? 18 : 23, fontWeight: 800, letterSpacing: '-0.02em', color: s.color }}>{s.value}</div>
              {s.badge && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: s.badge.bg, color: s.badge.color }}>{s.badge.text}</span>}
            </div>
            {!phone && <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 2 }}>{s.sub}</div>}
          </div>
        );
      })}
    </div>
  );
}

// On phone, a horizontally-scrolling grid table forces reading one row a
// column at a time by scrolling sideways. Below, each row becomes its own
// card instead — first column as the card's title, the rest as label/value
// pairs in a 2-column grid — so a whole record reads top-to-bottom with no
// horizontal scroll. Desktop keeps the original grid-table layout untouched.
function DataTableCards({ cols, rows, footer }) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {rows.map((r, ri) => (
          <div key={ri} style={{ border: '1px solid #EDEFF3', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: r.cells[0]?.color || '#111827', marginBottom: r.cells.length > 1 ? 10 : 0 }}>{r.cells[0]?.text}</div>
            {r.cells.length > 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 10 }}>
                {r.cells.slice(1).map((c, ci) => (
                  <div key={ci}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9CA3AF' }}>{cols[ci + 1]?.label}</div>
                    <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.4, color: c.color, fontWeight: c.weight }}>{c.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ padding: '20px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No rows match this filter.</div>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 12 }}>{footer}</div>
    </>
  );
}

// Strips common formatting (currency symbol, thousands separators, unit
// suffixes like "L"/"d"/"%") so a column's displayed text can be compared
// numerically when possible — lets every table sort correctly (₹160 L above
// ₹24 L, 90d above 7d) without every call site needing to supply a separate
// raw sort value alongside its display text.
function parseCellNumber(text) {
  if (text == null) return null;
  const cleaned = String(text).replace(/[₹,%LdCr\s]/g, '');
  if (cleaned === '' || cleaned === '—') return null;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}
function compareCells(a, b, dir) {
  const an = parseCellNumber(a?.text);
  const bn = parseCellNumber(b?.text);
  let cmp;
  if (an !== null && bn !== null) cmp = an - bn;
  else cmp = String(a?.text ?? '').localeCompare(String(b?.text ?? ''));
  return dir === 'desc' ? -cmp : cmp;
}

// Single "Filter" menu covering every field for a table in one control,
// instead of one dropdown per field — a field list on the left, that field's
// searchable multi-select checkbox list on the right, Clear/Apply at the
// bottom (matches the CRM Allotments page's own Filter by panel). Selections
// are staged in `draft` while the panel's open and only committed (one
// state update, one refetch on controlled tables) when Apply is pressed —
// Clear resets everything immediately and closes. `fields`: [{ label, key,
// options: [{value, count?}] }]. `active`/`onApplyAll` are keyed by `key`.
function FilterMenu({ fields, active, onApplyAll, onReset }) {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(fields[0]?.key ?? null);
  const [draft, setDraft] = useState(active);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const totalActive = fields.reduce((n, f) => n + (active[f.key]?.length || 0), 0);
  const openMenu = () => {
    setDraft(active);
    setSearch('');
    if (activeKey == null) setActiveKey(fields[0]?.key ?? null);
    setOpen(true);
  };

  const field = fields.find((f) => f.key === activeKey);
  const draftValues = draft[activeKey] || [];
  const toggleValue = (v) => setDraft((d) => ({ ...d, [activeKey]: draftValues.includes(v) ? draftValues.filter((x) => x !== v) : [...draftValues, v] }));
  const visibleOptions = field ? field.options.filter((o) => !search || o.value.toLowerCase().includes(search.toLowerCase())) : [];

  const apply = () => { onApplyAll(draft); setOpen(false); };
  const clearAll = () => { onReset(); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => (open ? setOpen(false) : openMenu())} className="sdn-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid ' + (totalActive > 0 ? '#BFDBFE' : '#EDEFF3'), background: totalActive > 0 ? '#EFF6FF' : '#FFFFFF', borderRadius: 8, fontSize: 12.5, cursor: 'pointer', color: totalActive > 0 ? '#1D4ED8' : '#111827', fontWeight: totalActive > 0 ? 600 : 400 }}>
        <span>Filter{totalActive > 0 ? ` (${totalActive})` : ''}</span>
        <span style={{ fontSize: 9, color: '#9CA3AF', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▼</span>
      </div>
      {open && (
        // zIndex kept below the sticky header's (30) — see Dropdown's old
        // note, same reasoning applies to every popup in this file.
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, display: 'flex', width: 300, maxWidth: 'calc(100vw - 32px)', background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 10, boxShadow: '0 10px 30px rgba(16,24,40,.14)', zIndex: 20, overflow: 'hidden' }}>
          <div style={{ width: 92, flex: '0 0 92px', borderRight: '1px solid #F1F3F7', maxHeight: 300, overflowY: 'auto' }}>
            {fields.map((f) => {
              const count = draft[f.key]?.length || 0;
              const isSel = f.key === activeKey;
              return (
                <div key={f.key} onClick={() => { setActiveKey(f.key); setSearch(''); }} className="sdn-hover-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, padding: '10px 10px', fontSize: 12, cursor: 'pointer', background: isSel ? '#F7F8FA' : 'transparent', borderLeft: '3px solid ' + (isSel ? '#2563EB' : 'transparent'), color: isSel ? '#111827' : '#374151', fontWeight: isSel ? 600 : 400 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                  {count > 0 && <span style={{ fontSize: 10, color: '#2563EB', fontWeight: 700, flex: '0 0 auto' }}>{count}</span>}
                </div>
              );
            })}
          </div>
          <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', maxHeight: 300 }}>
            <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F3F7' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                style={{ width: '100%', border: '1px solid #EDEFF3', borderRadius: 6, padding: '6px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '4px 0' }}>
              {visibleOptions.length === 0 && <div style={{ padding: '16px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No options</div>}
              {visibleOptions.map((opt) => {
                const checked = draftValues.includes(opt.value);
                return (
                  <div key={opt.value} onClick={() => toggleValue(opt.value)} className="sdn-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', fontSize: 12.5, cursor: 'pointer', color: checked ? '#2563EB' : '#111827', fontWeight: checked ? 600 : 400 }}>
                    <span style={{ width: 14, height: 14, borderRadius: 4, border: '1.5px solid ' + (checked ? '#2563EB' : '#D1D5DB'), background: checked ? '#2563EB' : '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                      {checked && <span style={{ color: '#FFFFFF', fontSize: 9, lineHeight: 1 }}>✓</span>}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.value}{opt.count != null ? ` (${opt.count})` : ''}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 10px', borderTop: '1px solid #F1F3F7', background: '#F9FAFB' }}>
              <div onClick={clearAll} className="sdn-hover-row" style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#374151', border: '1px solid #EDEFF3', cursor: 'pointer', background: '#FFFFFF' }}>Clear</div>
              <div onClick={apply} className="sdn-hover-darkblue" style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#FFFFFF', background: '#2563EB', cursor: 'pointer' }}>Apply</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Single-select "Sort by" menu — exposes the exact same column sort DataTable
// already drives from clicking a column header, just as a dropdown, so it
// also works on phone (the card layout has no header row to click). `value`
// is `"<colIndex>:<dir>"` or `"default"`; `options` are built per-table from
// `cols` (see DataTable).
function SortMenu({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen((o) => !o)} className="sdn-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid #EDEFF3', background: '#FFFFFF', borderRadius: 8, fontSize: 12.5, cursor: 'pointer', color: '#111827', maxWidth: 220 }}>
        <span style={{ color: '#9CA3AF', fontWeight: 600, flex: '0 0 auto' }}>Sort:</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current?.label}</span>
        <span style={{ fontSize: 9, color: '#9CA3AF', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flex: '0 0 auto' }}>▼</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, minWidth: 200, maxHeight: 280, overflowY: 'auto', background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 10, boxShadow: '0 10px 30px rgba(16,24,40,.14)', zIndex: 20 }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="sdn-hover-row"
              style={{ padding: '8px 12px', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap', background: opt.value === value ? '#EFF4FF' : 'transparent', color: opt.value === value ? '#2563EB' : '#111827', fontWeight: opt.value === value ? 600 : 400 }}
            >{opt.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Prev / numbered pages / Next, matching the style the user referenced
// earlier. Collapses long page ranges to "1 … 4 5 6 … 39" around the
// current page.
function TablePagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;
  const keep = new Set([1, totalPages]);
  for (let p = page - 1; p <= page + 1; p++) if (p > 1 && p < totalPages) keep.add(p);
  const sorted = [...keep].sort((a, b) => a - b);
  const withGaps = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) withGaps.push(`gap-${p}`);
    withGaps.push(p);
    prev = p;
  }
  const go = (p) => { if (!disabled) onPageChange(p); };
  // Right-aligned and deliberately small (same sizing on phone and desktop
  // — this is a page-number strip, not a primary action, so it shouldn't
  // compete with the table above it for attention or width). `disabled`
  // (set while a controlled refetch is in flight) dims it and blocks clicks
  // so a second click can't race the first page's response in.
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 10, flexWrap: 'wrap', opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div onClick={() => page > 1 && go(page - 1)} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: page > 1 ? 'pointer' : 'default', color: page > 1 ? '#111827' : '#D1D5DB', border: '1px solid #EDEFF3' }}>‹ Prev</div>
      {withGaps.map((p) => (typeof p === 'string'
        ? <div key={p} style={{ padding: '3px 2px', fontSize: 11, color: '#9CA3AF' }}>…</div>
        : <div key={p} onClick={() => go(p)} style={{ minWidth: 22, textAlign: 'center', padding: '3px 5px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: p === page ? '#111827' : 'transparent', color: p === page ? '#FFFFFF' : '#6B7280' }}>{p}</div>
      ))}
      <div onClick={() => page < totalPages && go(page + 1)} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: page < totalPages ? 'pointer' : 'default', color: page < totalPages ? '#111827' : '#D1D5DB', border: '1px solid #EDEFF3' }}>Next ›</div>
    </div>
  );
}

const LOCAL_PAGE_SIZE = 15;

// `filters` (local mode): [{ label, columnIndex }] — options/counts derived
// from this table's own already-fetched `rows`, filtered AND paginated
// client-side (LOCAL_PAGE_SIZE per page). Used by tables that are never
// capped, so the full real dataset is already on screen (e.g. Tokens' "By
// event", Site Visits' tables, Projects' "Value by project").
//
// `controlled` (backend-filtered + backend-paginated mode): { fields:
// [{label, key, options}], active (keyed by `key`), applyAll, reset,
// pagination: {page, totalPages, onPageChange} }. Used for the three tables
// whose full dataset is too big to ship in one response (Allotments,
// Agreements, Tokens' "held longest") — `rows` here are ALREADY the
// backend's filtered result for the CURRENT page, `options` come from the
// backend's own filterOptions (computed from its full dataset, not just
// this page), and `applyAll`/`onPageChange` update the parent's state to
// trigger a real refetch — see SalesDashboardNew's
// allotmentsFilters/agreementsFilters/tokensHeldStatuses and the matching
// *Page state. This is what makes filtering/paging those three tables
// accurate instead of just honest about being incomplete.
// (Column sort stays client-side even here, so on a capped table it only
// reorders the current page, not the full filtered set.)
function DataTable({ cols, grid, minWidth, rows, footer, phone, filters, capped, controlled, loading }) {
  const [localActiveFilters, setLocalActiveFilters] = useState({});
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const [localPage, setLocalPage] = useState(1);

  const activeFilters = controlled ? controlled.active : localActiveFilters;

  // Each active filter value is an array of picked values now (multi-select)
  // — empty/absent means that field isn't restricting anything, otherwise a
  // row matches if its cell is ANY of the picked values (OR within a field,
  // AND across fields). Local mode's own `key` is just its `label` — it has
  // no separate backend field name to key by.
  const filteredRows = controlled || !filters ? rows : rows.filter((r) =>
    filters.every((f) => {
      const want = activeFilters[f.label] || [];
      return want.length === 0 || want.includes(r.cells[f.columnIndex]?.text);
    })
  );
  const sortedRows = sort.col == null ? filteredRows
    : [...filteredRows].sort((a, b) => compareCells(a.cells[sort.col], b.cells[sort.col], sort.dir));

  const localTotalPages = Math.max(1, Math.ceil(sortedRows.length / LOCAL_PAGE_SIZE));
  const clampedLocalPage = Math.min(localPage, localTotalPages);
  const pageRows = controlled ? sortedRows : sortedRows.slice((clampedLocalPage - 1) * LOCAL_PAGE_SIZE, clampedLocalPage * LOCAL_PAGE_SIZE);

  const filterFields = controlled
    ? controlled.fields
    : filters?.map((f) => {
      const allValues = rows.map((r) => r.cells[f.columnIndex]?.text).filter((v) => v != null && v !== '');
      const options = [...new Set(allValues)].map((v) => ({ value: v, count: allValues.filter((x) => x === v).length }));
      return { label: f.label, key: f.label, options };
    });

  const hasActiveFilter = filterFields?.some((f) => (activeFilters[f.key] || []).length > 0);
  const onApplyAll = controlled
    ? controlled.applyAll
    : (newActive) => { setLocalActiveFilters((s) => ({ ...s, ...newActive })); setLocalPage(1); };
  const onReset = controlled ? controlled.reset : () => { setLocalActiveFilters({}); setLocalPage(1); };

  // "Sort by" exposes the same per-column sort as clicking a header, as a
  // dropdown — the only way to sort on phone, where the card layout has no
  // header row to click (see SortMenu above). Built straight off `cols`, one
  // Ascending/Descending pair per column.
  const sortOptions = [
    { value: 'default', label: 'Default order' },
    ...cols.flatMap((c, i) => [
      { value: `${i}:asc`, label: `${c.label} (Ascending)` },
      { value: `${i}:desc`, label: `${c.label} (Descending)` },
    ]),
  ];
  const sortValue = sort.col == null ? 'default' : `${sort.col}:${sort.dir}`;
  const onSortMenuChange = (v) => {
    if (v === 'default') { setSort({ col: null, dir: 'asc' }); return; }
    const [ci, dir] = v.split(':');
    setSort({ col: Number(ci), dir });
  };

  const controlsRow = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 16 }}>
      {filterFields && filterFields.length > 0 && (
        <FilterMenu fields={filterFields} active={activeFilters} onApplyAll={onApplyAll} onReset={onReset} />
      )}
      <SortMenu value={sortValue} options={sortOptions} onChange={onSortMenuChange} />
      {!controlled && capped && hasActiveFilter && (
        <div style={{ fontSize: 10.5, color: '#B45309', width: '100%' }}>Filtering within the {rows.length} rows already loaded above — not the full underlying set.</div>
      )}
    </div>
  );
  const pagination = controlled?.pagination
    ? <TablePagination page={controlled.pagination.page} totalPages={controlled.pagination.totalPages} onPageChange={controlled.pagination.onPageChange} disabled={loading} />
    : <TablePagination page={clampedLocalPage} totalPages={localTotalPages} onPageChange={setLocalPage} />;

  // Shown over the table body while a controlled (backend) refetch is in
  // flight — a filter or page change doesn't visibly change anything until
  // the response lands, so without this it's impossible to tell whether a
  // click actually did anything.
  const loadingOverlay = controlled && loading && (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 8 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #E5E7EB', borderTopColor: '#2563EB', animation: 'vjSpin 0.7s linear infinite' }} />
    </div>
  );

  if (phone) return (
    <>
      {controlsRow}
      <div style={{ position: 'relative' }}>
        {loadingOverlay}
        <DataTableCards cols={cols} rows={pageRows} footer={footer} />
      </div>
      {pagination}
    </>
  );

  const onSort = (colIndex) => setSort((s) => ({ col: colIndex, dir: s.col === colIndex && s.dir === 'asc' ? 'desc' : 'asc' }));

  return (
    <>
      {controlsRow}
      <div style={{ position: 'relative' }}>
        {loadingOverlay}
        <div className="om-scroll" style={{ overflowX: 'auto', marginTop: 16 }}>
          <div style={{ minWidth }}>
            <div style={{ display: 'grid', alignItems: 'center', borderBottom: '1px solid #EDEFF3', paddingBottom: 10, gridTemplateColumns: grid }}>
              {cols.map((c, i) => (
                <div key={i} onClick={() => onSort(i)} className="sdn-hover-sortcol" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start', fontSize: 12.5, fontWeight: 700, color: sort.col === i ? '#2563EB' : '#6B7280', paddingRight: 12, whiteSpace: 'nowrap', textAlign: c.align, cursor: 'pointer', userSelect: 'none' }}>
                  {c.label}
                  <span style={{ fontSize: 10, opacity: sort.col === i ? 1 : 0.35 }}>{sort.col === i && sort.dir === 'desc' ? '▼' : '▲'}</span>
                </div>
              ))}
            </div>
            {pageRows.map((r, ri) => (
              <div key={ri} className="sdn-hover-row" style={{ display: 'grid', alignItems: 'center', borderBottom: '1px solid #F1F3F7', padding: '12px 0', gridTemplateColumns: grid }}>
                {r.cells.map((c, ci) => (
                  <div key={ci} style={{ fontSize: 12.5, paddingRight: 12, lineHeight: 1.4, textAlign: c.align, color: c.color, fontWeight: c.weight }}>{c.text}</div>
                ))}
              </div>
            ))}
            {pageRows.length === 0 && (
              <div style={{ padding: '24px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No rows match this filter.</div>
            )}
          </div>
        </div>
      </div>
      {pagination}
      <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 12 }}>{footer}</div>
    </>
  );
}

function BarsList({ bars, note, onBarClick }) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 16 }}>
        {bars.map((b, i) => {
          const clickable = !!onBarClick && b.dimValue != null;
          return (
          <div key={i} onClick={clickable ? () => onBarClick(b) : undefined} className={clickable ? 'sdn-hover-row' : undefined} style={{ cursor: clickable ? 'pointer' : 'default' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, flex: '0 0 10px', borderRadius: 3, background: b.color }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>{b.label}</span>
              </div>
              <div style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 600, color: '#111827' }}>{b.value}</span>{' '}
                <span style={{ color: '#9CA3AF' }}>{b.sub}</span>
              </div>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: '#F1F3F7' }}>
              <div style={{ height: 8, borderRadius: 999, background: b.color, width: b.width }} />
            </div>
          </div>
          );
        })}
      </div>
      {note && <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 12, lineHeight: 1.5 }}>{note}</div>}
    </>
  );
}

// Phone-only vertical rebuild of the journey funnel's stage cards. The
// desktop version relies on horizontal connectors/arrows and side-by-side
// forks (see the "the journey funnel" render below) that were never adapted
// for narrow screens — everything overlapped once squeezed sideways. This
// renders the same numbers top-to-bottom instead: one full-width card per
// stage, connected by a simple vertical arrow.
// Sized a step down from the desktop stage cards (which run a big 23px
// value + generous padding) — at this step count, five full-size cards
// stacked vertically read as oversized and made the fork boxes in between
// look like an afterthought by comparison. This keeps step 1/2/4/5 in
// proportion with the fork's own Tokens/Sustenance boxes. Same hover lift
// as the desktop cards (sdn-hover-card).
function JourneyStageCard({ step, icon, label, value, sub, delta, deltaColor, color, tint, border }) {
  return (
    <div className="sdn-hover-card" style={{ borderRadius: 12, padding: '9px 11px', background: tint, border: `1px solid ${border}`, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ width: 22, height: 22, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={12} color={color} /></div>
        <div style={{ width: 16, height: 16, borderRadius: 999, background: color, color: '#FFFFFF', fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step}</div>
      </div>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginTop: 8 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 2, color }}>{value}</div>
      {(sub || delta) && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {sub && <div style={{ fontSize: 10.5, color: '#6B7280' }}>{sub}</div>}
          {delta && <div style={{ fontSize: 10.5, fontWeight: 600, color: deltaColor }}>{delta}</div>}
        </div>
      )}
    </div>
  );
}

// A short connector between two stacked journey stages — percentage pill on
// the left, a dotted downward arrow in the middle, caption on the right,
// same arrangement for every connector. The dashes themselves scroll
// downward (animating the dash pattern's own background-position, not a
// separate glow sliding over a static line) so the direction of flow is
// unambiguous even over this short a distance.
function JourneyConnector({ pct, note, color, to }) {
  const c = color || '#D1D5DB';
  const lineColor = to || c;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '4px 0' }}>
      {pct
        ? <div style={{ padding: '3px 9px', borderRadius: 999, background: '#FFFFFF', border: `1px solid ${c}`, color: c, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{pct}</div>
        : <div style={{ width: 44 }} />}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <div style={{
          width: 3, height: 16, borderRadius: 3,
          backgroundImage: `repeating-linear-gradient(180deg, ${lineColor} 0 3px, transparent 3px 6px)`,
          backgroundSize: '100% 6px', backgroundRepeat: 'repeat-y',
          animation: 'vjDotsFlow 0.6s linear infinite',
        }} />
        <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${lineColor}` }} />
      </div>
      <div style={{ fontSize: 9.5, color: '#9CA3AF', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{note || ''}</div>
    </div>
  );
}

// A single stage's "N days median, against the company" box-plot card —
// grey track 0..axisMax, a blue band for the middle half (p25-p75), a dark
// tick for this scope's own median, a grey tick for the company median.
function TimingCard({ label, stat, totalBooked }) {
  if (!stat.own) {
    return (
      <div style={{ flex: '1 1 260px', minWidth: 240, borderRadius: 12, border: '1px solid #EDEFF3', background: '#F7F8FA', padding: 16 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>{label}</div>
        <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 14 }}>Not enough data this period.</div>
      </div>
    );
  }
  const { own, companyMedian, axisMax } = stat;
  const pos = (v) => Math.max(0, Math.min(100, (v / axisMax) * 100));
  const diff = companyMedian != null ? companyMedian - own.median : null;
  const deltaColor = diff == null ? '#6B7280' : diff > 0 ? '#16A34A' : diff < 0 ? '#DC2626' : '#6B7280';
  const deltaText = diff == null
    ? 'No company benchmark yet'
    : diff > 0 ? '▼ ' + diff + ' days faster than company (' + companyMedian + ')'
    : diff < 0 ? '▲ ' + (-diff) + ' days slower than company (' + companyMedian + ')'
    : 'Same as company (' + companyMedian + ')';
  return (
    <div style={{ flex: '1 1 260px', minWidth: 240, borderRadius: 12, border: '1px solid #EDEFF3', background: '#F7F8FA', padding: 16 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: '#111827' }}>{own.median}</span>
          <span style={{ fontSize: 12.5, color: '#6B7280' }}>days median</span>
        </div>
        <div style={{ fontSize: 11.5, color: '#9CA3AF', whiteSpace: 'nowrap' }}>average {own.mean} days</div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4 }}>{deltaText}</div>
      <div style={{ position: 'relative', marginTop: 16, height: 8, borderRadius: 999, background: '#E5E7EB' }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 999, background: '#93C5FD', left: pos(own.p25) + '%', width: Math.max(0, pos(own.p75) - pos(own.p25)) + '%' }} />
        <div style={{ position: 'absolute', top: -3, bottom: -3, width: 2, background: '#111827', left: pos(own.median) + '%' }} />
        {companyMedian != null && <div style={{ position: 'absolute', top: -3, bottom: -3, width: 2, background: '#9CA3AF', left: pos(companyMedian) + '%' }} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10.5, color: '#9CA3AF' }}>
        <span>0d</span>
        <span>middle half {own.p25}-{own.p75}d</span>
        <span>{axisMax}d</span>
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10 }}>Measurable on {own.n} of {totalBooked} bookings. Company median marked in grey.</div>
    </div>
  );
}

function TimingRow({ timing, totalBooked }) {
  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
      <TimingCard label="Site visit → booking" stat={timing.visitToBooking} totalBooked={totalBooked} />
      <TimingCard label="Booking → agreement" stat={timing.bookingToAgreement} totalBooked={totalBooked} />
    </div>
  );
}

// Tiny inline trend line — no axes, just shape, matching each row's own
// swatch color.
function Sparkline({ values, color }) {
  const width = 90, height = 26;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(1, max - min);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => (i * stepX).toFixed(1) + ',' + (height - ((v - min) / range) * height).toFixed(1)).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const MONTHLY_ROWS = [
  { key: 'leads', label: 'Leads', color: '#EC4899' },
  { key: 'calls', label: 'Calls', color: '#8B5CF6' },
  { key: 'visits', label: 'Site Visits', color: '#F97316' },
  { key: 'allot', label: 'Bookings', color: '#2563EB' },
  { key: 'agreements', label: 'Agreements', color: '#16A34A' },
  { key: 'salesValueCr', label: 'Sales Value ₹Cr', color: '#D97706' },
];

function MonthlyTrendTable({ monthly }) {
  const gridCols = '150px repeat(' + monthly.length + ',minmax(46px,1fr)) 100px';
  return (
    <div className="om-scroll" style={{ overflowX: 'auto', marginTop: 16 }}>
      <div style={{ minWidth: Math.max(720, monthly.length * 56 + 260) }}>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 4, borderBottom: '1px solid #EDEFF3', paddingBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280' }}>Metric</div>
          {monthly.map((m, i) => <div key={i} style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textAlign: 'right' }}>{m.month}</div>)}
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280', textAlign: 'right' }}>Trend</div>
        </div>
        {MONTHLY_ROWS.map((row) => {
          const values = monthly.map((m) => m[row.key]);
          return (
            <div key={row.key} className="sdn-hover-row" style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 4, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F3F7' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: '#111827' }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: row.color, flex: '0 0 8px' }} />
                {row.label}
              </div>
              {values.map((v, i) => <div key={i} style={{ fontSize: 12.5, textAlign: 'right', color: '#374151' }}>{row.key === 'salesValueCr' ? v.toFixed(1) : int(v)}</div>)}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Sparkline values={values} color={row.color} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Donut + legend list — By source / By project / By unit type all share
// this shape. Colors cycle CAT by position, same rotating categorical
// palette every other breakdown on this dashboard uses. Capped to a top-N
// + "Other" legend backend-side (performanceController.js) so this never
// has to render more rows than a person can actually read.
function DonutCard({ items }) {
  const data = {
    labels: items.map((it) => it.label),
    datasets: [{ data: items.map((it) => it.count), backgroundColor: items.map((_, i) => CAT[i % CAT.length]), borderWidth: 0 }],
  };
  const options = { cutout: '68%', maintainAspectRatio: false, plugins: { legend: { display: false } } };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
      <div style={{ width: 108, height: 108, flex: '0 0 108px' }}><Doughnut data={data} options={options} /></div>
      <div style={{ flex: '1 1 140px', minWidth: 120, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: CAT[i % CAT.length], flex: '0 0 9px' }} />
              <span style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
            </div>
            <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 700, color: '#111827' }}>{int(it.count)}</span>{' '}
              <span style={{ color: '#9CA3AF' }}>{it.pct}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// By source / By project / By unit type, side by side in one row (wraps to
// fewer columns on a narrower screen) — each keeps its own mini header,
// matching the reference design instead of three full-width cards stacked
// one below the other.
function DonutRow({ items }) {
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))' }}>
      {items.map((it, i) => (
        <div key={i} style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: '16px 18px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{it.title}</div>
          <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3 }}>{it.sub}</div>
          <DonutCard items={it.items} />
        </div>
      ))}
    </div>
  );
}

// One stacked horizontal bar (Event / PreSelect / Sustenance / Other),
// segments sized by share of the total, legend below.
function BookingTypeBar({ items }) {
  const total = items.reduce((s, it) => s + it.count, 0) || 1;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden' }}>
        {items.map((it, i) => {
          const pct = pctOf(it.count, total);
          return (
            <div key={i} title={it.type + ': ' + it.count} style={{ width: pct + '%', background: CAT[i % CAT.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 12.5, fontWeight: 700 }}>
              {pct >= 8 ? it.count : ''}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: CAT[i % CAT.length] }} />
            {it.type} <span style={{ fontWeight: 700, color: '#111827' }}>{it.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Real status → color, matching the reference design's TONE map exactly
// for the statuses it names, extended to the rest of this app's real
// BOOKING_STATUSES/CANCEL_STATUSES lists so nothing falls back unstyled.
const PROJECT_STATUS_TONE = {
  'Payment Complete': '#16A34A', 'Agreement Done': '#16A34A',
  'Partial Payment Done': '#D97706', 'Payment Pending': '#D97706',
  'Cancellation Pending': '#DC2626', 'Cancellation Not Done': '#DC2626', 'Cancellation Requested': '#DC2626',
  Cancelled: '#DC2626', 'Refund Initiated': '#DC2626', 'Refund Processed': '#DC2626',
};
const PROJECT_STATUS_ORDER = [
  'Payment Pending', 'Partial Payment Done', 'Payment Complete', 'Agreement Done',
  'Cancellation Pending', 'Cancellation Requested', 'Cancellation Not Done', 'Cancelled', 'Refund Initiated', 'Refund Processed',
];

// "Project details" — every project this scope touched, sticky first
// column, a mini bar under each project name sized by its own leads count
// relative to the busiest project, and a "View details" button opening the
// per-project allotment drill-down.
// Same card-per-row pattern as DataTableCards: project name + its lead-share
// bar as the card's title, the six metrics as label/value pairs, and a
// full-width "View details" button in place of the desktop table's own
// button column. Desktop keeps the original grid table untouched.
function ProjectDetailsCards({ rows, totals, onOpen }) {
  const METRICS = ['Leads', 'Calls', 'Site Visits', 'Tokens', 'Allotment', 'Agreement Done'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
      {rows.map((p, i) => {
        const values = [int(p.leads), int(p.calls), int(p.visits), int(p.tokens), int(p.allot), p.registered ? int(p.registered) : '—'];
        return (
          <div key={i} style={{ border: '1px solid #EDEFF3', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</div>
            <div style={{ height: 4, borderRadius: 999, background: '#F1F3F7', marginTop: 7 }}>
              <div style={{ height: 4, borderRadius: 999, background: '#2563EB', width: p.leadsBarPct + '%' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 10, marginTop: 12 }}>
              {METRICS.map((label, mi) => (
                <div key={label}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9CA3AF' }}>{label}</div>
                  <div style={{ fontSize: 12.5, marginTop: 2, color: mi === 1 ? '#9CA3AF' : (mi === 5 && !p.registered) ? '#9CA3AF' : '#111827', fontWeight: mi === 4 ? 600 : 400 }}>{values[mi]}</div>
                </div>
              ))}
            </div>
            <div onClick={() => onOpen(p)} className="sdn-hover-info" style={{ marginTop: 12, padding: '8px 0', border: '1px solid #EDEFF3', borderRadius: 8, fontSize: 12.5, fontWeight: 600, color: '#2563EB', cursor: 'pointer', textAlign: 'center' }}>View details</div>
          </div>
        );
      })}
      <div style={{ border: '1px solid #EDEFF3', borderRadius: 12, padding: '12px 14px', background: '#F7F8FA' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Total</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 10, marginTop: 10 }}>
          {METRICS.map((label, mi) => (
            <div key={label}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9CA3AF' }}>{label}</div>
              <div style={{ fontSize: 12.5, marginTop: 2, fontWeight: 700, color: mi === 1 ? '#9CA3AF' : '#111827' }}>
                {[int(totals.leads), int(totals.calls), int(totals.visits), int(totals.tokens), int(totals.allot), int(totals.registered)][mi]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Fields a project row can be filtered/sorted on — shared by the Filter and
// Sort menus below. `key` indexes the row object directly (these are plain
// {name, leads, calls, ...} objects, not DataTable's {cells:[...]} shape, so
// sorting compares the raw numbers/strings instead of parsing display text.
const PROJECT_DETAIL_SORT_FIELDS = [
  { key: 'name', label: 'Project' },
  { key: 'leads', label: 'Leads' },
  { key: 'calls', label: 'Calls' },
  { key: 'visits', label: 'Site Visits' },
  { key: 'tokens', label: 'Tokens' },
  { key: 'allot', label: 'Allotment' },
  { key: 'registered', label: 'Agreement Done' },
];

function ProjectDetailsTable({ rows, totals, onOpen, phone }) {
  const [projectFilter, setProjectFilter] = useState([]);
  const [sort, setSort] = useState('default');

  const filteredRows = projectFilter.length ? rows.filter((r) => projectFilter.includes(r.name)) : rows;
  const sortedRows = sort === 'default' ? filteredRows : (() => {
    const [key, dir] = sort.split(':');
    return [...filteredRows].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
      return dir === 'desc' ? -cmp : cmp;
    });
  })();
  // Totals follow whatever's actually filtered, so the footer row never
  // shows the whole scope's grand total while the rows above it are a
  // narrowed-down subset — only recomputed (not just reused as-is) once a
  // filter is active, since the passed-in `totals` already equals the
  // unfiltered sum.
  const shownTotals = projectFilter.length
    ? filteredRows.reduce((acc, r) => ({
      leads: acc.leads + (r.leads || 0), calls: acc.calls + (r.calls || 0), visits: acc.visits + (r.visits || 0),
      tokens: acc.tokens + (r.tokens || 0), allot: acc.allot + (r.allot || 0), registered: acc.registered + (r.registered || 0),
    }), { leads: 0, calls: 0, visits: 0, tokens: 0, allot: 0, registered: 0 })
    : totals;

  const filterFields = [{ label: 'Project', key: 'project', options: [...new Set(rows.map((r) => r.name))].sort().map((v) => ({ value: v })) }];
  const sortOptions = [
    { value: 'default', label: 'Default order' },
    ...PROJECT_DETAIL_SORT_FIELDS.flatMap((f) => [
      { value: `${f.key}:asc`, label: `${f.label} (Ascending)` },
      { value: `${f.key}:desc`, label: `${f.label} (Descending)` },
    ]),
  ];
  const controlsRow = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 16 }}>
      <FilterMenu
        fields={filterFields}
        active={{ project: projectFilter }}
        onApplyAll={(newActive) => setProjectFilter(newActive.project || [])}
        onReset={() => setProjectFilter([])}
      />
      <SortMenu value={sort} options={sortOptions} onChange={setSort} />
    </div>
  );

  if (phone) return (
    <>
      {controlsRow}
      <ProjectDetailsCards rows={sortedRows} totals={shownTotals} onOpen={onOpen} />
    </>
  );
  const gridCols = '220px minmax(56px,0.7fr) minmax(56px,0.7fr) minmax(74px,0.9fr) minmax(66px,0.8fr) minmax(78px,0.9fr) minmax(122px,1.2fr) 112px';
  return (
    <>
      {controlsRow}
      <div className="om-scroll" style={{ overflowX: 'auto', marginTop: 16 }}>
      <div style={{ minWidth: 1060 }}>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 10, alignItems: 'center', borderBottom: '1px solid #EDEFF3', paddingBottom: 10 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, position: 'sticky', left: 0, background: '#FFFFFF' }}>Project</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>Leads</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', color: '#9CA3AF' }}>Calls</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>Site Visits</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>Tokens</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>Allotment</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>Agreement Done</div>
          <div />
        </div>
        {sortedRows.map((p, i) => (
          <div key={i} className="sdn-hover-row" style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 10, alignItems: 'center', borderBottom: '1px solid #F1F3F7', padding: '12px 0' }}>
            <div style={{ position: 'sticky', left: 0, background: '#FFFFFF', paddingRight: 16, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
              <div style={{ height: 4, borderRadius: 999, background: '#F1F3F7', marginTop: 7 }}>
                <div style={{ height: 4, borderRadius: 999, background: '#2563EB', width: p.leadsBarPct + '%' }} />
              </div>
            </div>
            <div style={{ fontSize: 13, textAlign: 'right' }}>{int(p.leads)}</div>
            <div style={{ fontSize: 13, textAlign: 'right', color: '#9CA3AF' }}>{int(p.calls)}</div>
            <div style={{ fontSize: 13, textAlign: 'right' }}>{int(p.visits)}</div>
            <div style={{ fontSize: 13, textAlign: 'right' }}>{int(p.tokens)}</div>
            <div style={{ fontSize: 13, textAlign: 'right', fontWeight: 600 }}>{int(p.allot)}</div>
            <div style={{ fontSize: 13, textAlign: 'right', color: p.registered ? '#111827' : '#9CA3AF' }}>{p.registered ? int(p.registered) : '—'}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div onClick={() => onOpen(p)} className="sdn-hover-info" style={{ padding: '6px 11px', border: '1px solid #EDEFF3', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#2563EB', cursor: 'pointer', whiteSpace: 'nowrap' }}>View details</div>
            </div>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 10, alignItems: 'center', padding: '13px 0 0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, position: 'sticky', left: 0, background: '#FFFFFF' }}>Total</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10 }}>{int(shownTotals.leads)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10, color: '#9CA3AF' }}>{int(shownTotals.calls)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10 }}>{int(shownTotals.visits)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10 }}>{int(shownTotals.tokens)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10 }}>{int(shownTotals.allot)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', paddingRight: 10 }}>{int(shownTotals.registered)}</div>
          <div />
        </div>
      </div>
      </div>
    </>
  );
}

// The "View details" drill-down — one project's allotments, filterable by
// status, matching the reference design's modal exactly.
function ProjectDetailModal({ projectName, subtitle, rows, statusFilter, onStatusFilter, onClose }) {
  const statuses = ['All', ...PROJECT_STATUS_ORDER]
    .map((label) => ({ label, count: label === 'All' ? rows.length : rows.filter((r) => r.status === label).length }))
    .filter((s) => s.count > 0);
  const shown = statusFilter === 'All' ? rows : rows.filter((r) => r.status === statusFilter);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#FFFFFF', borderRadius: 14, width: 'min(960px,96vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(16,24,40,.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '20px 24px 0' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{projectName}</div>
            <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 4 }}>{subtitle}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '16px 24px 0' }}>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>Status</span>
          {statuses.map((s) => {
            const on = s.label === statusFilter;
            const tone = PROJECT_STATUS_TONE[s.label] || '#111827';
            return (
              <div key={s.label} onClick={() => onStatusFilter(s.label)} style={{
                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                color: on ? '#FFFFFF' : (s.label === 'All' ? '#6B7280' : tone),
                background: on ? (s.label === 'All' ? '#111827' : tone) : '#F7F8FA',
                border: '1px solid ' + (on ? (s.label === 'All' ? '#111827' : tone) : '#EDEFF3'),
              }}>{s.label} {s.count}</div>
            );
          })}
        </div>
        <div className="om-scroll" style={{ overflow: 'auto', padding: '16px 24px', flex: '1 1 auto' }}>
          {/* A real <table> here (not per-row CSS grids) so columns actually line
              up — a table's own layout sizes each column from every row's content
              at once, unlike separate grids which would each size independently
              and drift out of alignment. Cells stay single-line (no truncation,
              full names) and this wrapper's overflow:auto scrolls sideways once
              the table's natural width outgrows the modal. */}
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #EDEFF3' }}>
                {['Allotment ID', 'Customer name', 'Unit', 'BSP', 'Source', 'CP name', 'FOS', 'Status'].map((h, ci, arr) => (
                  <th key={h} style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280', whiteSpace: 'nowrap', textAlign: 'left', padding: '0 16px 10px', borderRight: ci < arr.length - 1 ? '1px solid #EDEFF3' : 'none' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const cellStyle = (last) => ({ padding: '11px 16px', borderRight: last ? 'none' : '1px solid #F1F3F7', whiteSpace: 'nowrap' });
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #F1F3F7' }}>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, color: '#6B7280' }}>{r.leadDisplayId || '—'}</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, fontWeight: 600, color: '#111827' }}>{r.customerName || r.leadDisplayId || '—'}</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, color: '#6B7280' }}>{r.unitNo || '—'}</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, fontWeight: 600, color: '#111827' }}>₹{int(r.bspL)} L</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, color: '#6B7280' }}>{r.source}</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, color: '#6B7280' }}>{r.cpName || '—'}</td>
                    <td style={{ ...cellStyle(false), fontSize: 12.5, color: '#6B7280' }}>{r.fosName || '—'}</td>
                    <td style={{ ...cellStyle(true), fontSize: 12.5, fontWeight: 600, color: PROJECT_STATUS_TONE[r.status] || '#111827' }}>{r.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No allotments with this status in this period.</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 24px', borderTop: '1px solid #F1F3F7' }}>
          <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>Showing {shown.length} of {rows.length} allotments · customer phone numbers are never shown or sent</div>
          <div onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, background: '#2563EB', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</div>
        </div>
      </div>
    </div>
  );
}

// §8's drill-down modal — the reduced leads behind whichever figure was
// clicked, plus one level deeper (the breakdown). Never phone, identity or
// street address — see personaController.js's file header §4.
const PERSONA_DRILLDOWN_COLS = [
  ['Lead ID', 'left', 'minmax(0,1fr)'], ['Name', 'left', 'minmax(0,1.3fr)'], ['Age/Gender', 'left', 'minmax(0,0.9fr)'],
  ['Occupation', 'left', 'minmax(0,1.3fr)'], ['Income', 'left', 'minmax(0,0.9fr)'], ['Home area', 'left', 'minmax(0,1.3fr)'],
  ['State', 'left', 'minmax(0,1fr)'], ['Work area', 'left', 'minmax(0,1.3fr)'], ['Employer', 'left', 'minmax(0,1fr)'],
  ['Score', 'right', '70px'], ['Temp', 'left', '70px'], ['Tags', 'left', 'minmax(0,1.6fr)'],
];
const PERSONA_DRILLDOWN_GRID = PERSONA_DRILLDOWN_COLS.map((c) => c[2]).join(' ');

function PersonaDrilldownModal({ title, data, loading, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#FFFFFF', borderRadius: 14, width: 'min(1180px,96vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(16,24,40,.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '20px 24px 0' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</div>
            <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 4 }}>
              {loading ? 'Loading…' : data ? int(data.count) + ' lead' + (data.count === 1 ? '' : 's') + (data.leadsShown < data.count ? ' · showing ' + int(data.leadsShown) : '') : ''}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</div>
        </div>

        {!loading && data && data.breakdown.rows.length > 0 && (
          <div style={{ padding: '14px 24px 0' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{data.breakdown.title}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.breakdown.rows.slice(0, 12).map((b, i) => (
                <div key={i} style={{ padding: '5px 11px', borderRadius: 999, background: '#F7F8FA', border: '1px solid #EDEFF3', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                  {b.label} <span style={{ fontWeight: 700, color: '#111827' }}>{int(b.n)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="om-scroll" style={{ overflow: 'auto', padding: '16px 24px', flex: '1 1 auto' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading…</div>
          ) : (
            <div style={{ minWidth: 1080 }}>
              <div style={{ display: 'grid', gridTemplateColumns: PERSONA_DRILLDOWN_GRID, gap: 8, borderBottom: '1px solid #EDEFF3', paddingBottom: 10 }}>
                {PERSONA_DRILLDOWN_COLS.map((c) => (
                  <div key={c[0]} style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280', textAlign: c[1] }}>{c[0]}</div>
                ))}
              </div>
              {(data?.leads || []).map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: PERSONA_DRILLDOWN_GRID, gap: 8, alignItems: 'center', padding: '11px 0', borderBottom: '1px solid #F1F3F7' }}>
                  <div style={{ fontSize: 12.5, color: '#6B7280' }}>{l.displayId || '—'}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280' }}>{l.age || '—'}{l.gender ? ' · ' + l.gender : ''}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.occupation || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280' }}>{l.incomeBand || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.homeArea || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.state || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.workArea || '—'}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.employer || '—'}</div>
                  <div title={l.scoreBreakdown ? Object.entries(l.scoreBreakdown).map(([k, v]) => k + ': ' + v).join(' · ') : ''} style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', color: l.score >= 70 ? '#16A34A' : '#111827' }}>{l.score}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280' }}>{l.temperature || '—'}</div>
                  <div style={{ fontSize: 11.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(l.tags || []).join(', ') || '—'}</div>
                </div>
              ))}
              {data && data.leads.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No leads match this figure.</div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 24px', borderTop: '1px solid #F1F3F7' }}>
          <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>No phone numbers or street addresses are shown or sent · hover a score for its breakdown</div>
          <div onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, background: '#2563EB', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Close</div>
        </div>
      </div>
    </div>
  );
}

function NotePanel({ title, open, onToggle, showBox, noteText, noteBg, noteBorder, noteColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0 2px' }}>
      <InfoToggle open={open} onClick={onToggle} size={26} iconSize={14} title="Show this note" />
      <div style={{ minWidth: 0, paddingTop: 4 }}>
        <div onClick={onToggle} className="sdn-hover-titledark" style={{ fontSize: 12.5, fontWeight: 600, color: '#6B7280', cursor: 'pointer' }}>{title}</div>
        {showBox && (
          <div style={{ marginTop: 9, padding: '13px 15px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.6, maxWidth: '88ch', background: noteBg, border: `1px solid ${noteBorder}`, color: noteColor }}>{noteText}</div>
        )}
      </div>
    </div>
  );
}

// Shown for any tab with no real backend endpoint yet, and (with `loading`)
// for Site Visits before its own fetch has resolved — an honest state
// instead of ever falling back to invented numbers.
function NotConnectedPanel({ title, loading }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: 20 }}>
      <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 10, lineHeight: 1.6 }}>
        {loading ? 'Loading…' : 'This section isn’t connected to real data yet.'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SalesDashboardNew({ initialTab, period: initialPeriod }) {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const [tab, setTab] = useState(initialTab || 'Overview');
  const [period, setPeriod] = useState(initialPeriod || 'This Month');
  const [hover, setHover] = useState(null);
  const [notes, setNotes] = useState({});
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 27 * 86400000).toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);

  // Real data for the logged-in user's Overview tab.
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Same idea for the Site Visits tab, fetched lazily the first time that
  // tab is opened (and again if the period changes while it's open).
  const [siteVisits, setSiteVisits] = useState(null);
  const [siteVisitsLoading, setSiteVisitsLoading] = useState(false);

  // Same idea for Leads & Calls.
  const [leadsCalls, setLeadsCalls] = useState(null);
  const [leadsCallsLoading, setLeadsCallsLoading] = useState(false);

  // Same idea for Tokens.
  const [tokens, setTokens] = useState(null);
  const [tokensLoading, setTokensLoading] = useState(false);
  // The "held longest" table's Status filter — lifted up here (rather than
  // living inside DataTable) because changing it has to trigger a real
  // refetch with heldStatus= so the backend re-filters before its own
  // TABLE_CAP slice, instead of just filtering whatever 10 rows already
  // loaded. See DataTable's `controlled` mode.
  // Multi-select — a list of picked statuses, empty meaning "All".
  const [tokensHeldStatuses, setTokensHeldStatuses] = useState([]);
  // The same table's page — reset to 1 whenever the Status filter changes
  // (see the effect below) so a filter change never leaves the view on a
  // page number that no longer exists in the newly filtered set.
  const [tokensHeldPage, setTokensHeldPage] = useState(1);

  // Same idea for Allotments. Each field is a list of picked values now
  // (multi-select) — empty means that field isn't restricting anything.
  const [allotments, setAllotments] = useState(null);
  const [allotmentsLoading, setAllotmentsLoading] = useState(false);
  const [allotmentsFilters, setAllotmentsFilters] = useState({ project: [], cp: [], fos: [], status: [] });
  const [allotmentsPage, setAllotmentsPage] = useState(1);

  // Same idea for Agreements.
  const [agreements, setAgreements] = useState(null);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [agreementsFilters, setAgreementsFilters] = useState({ project: [], cp: [], fos: [] });
  const [agreementsPage, setAgreementsPage] = useState(1);

  // Same idea for Performance.
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  // Same idea for Persona.
  const [persona, setPersona] = useState(null);
  const [personaLoading, setPersonaLoading] = useState(false);

  // The Persona tab's drill-down modal (§8) — which figure was clicked
  // (null when closed) and its fetched result.
  const [personaDrilldown, setPersonaDrilldown] = useState(null);
  const [personaDrilldownData, setPersonaDrilldownData] = useState(null);
  const [personaDrilldownLoading, setPersonaDrilldownLoading] = useState(false);

  // Same idea for Projects.
  const [projects, setProjects] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // The "View details" drill-down modal on the Projects tab — which
  // project is open (null when closed) and its own status filter.
  const [openProject, setOpenProject] = useState(null);
  const [projectStatusFilter, setProjectStatusFilter] = useState('All');

  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    // Deliberately NOT cleared to null before the fetch — the last-loaded
    // numbers stay on screen (rather than the whole tab blanking to a
    // placeholder) while a small spinner next to the period label (see
    // currentTabLoading below) signals a refresh is in flight.
    setOverviewLoading(true);
    getDashboardOverview(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setOverview(res.data); })
      .catch((err) => console.error('Failed to load dashboard overview:', err))
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [period, fromDate, toDate, loggedInUser?.userId]);

  useEffect(() => {
    if (tab !== 'Site Visits' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setSiteVisitsLoading(true);
    getDashboardSiteVisits(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setSiteVisits(res.data); })
      .catch((err) => console.error('Failed to load site visits:', err))
      .finally(() => { if (!cancelled) setSiteVisitsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId]);

  useEffect(() => {
    if (tab !== 'Leads & Calls' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setLeadsCallsLoading(true);
    getDashboardLeadsCalls(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setLeadsCalls(res.data); })
      .catch((err) => console.error('Failed to load leads & calls:', err))
      .finally(() => { if (!cancelled) setLeadsCallsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId]);

  useEffect(() => {
    if (tab !== 'Tokens' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setTokensLoading(true);
    getDashboardTokens(loggedInUser.userId, startMs, endMs, tokensHeldStatuses, tokensHeldPage)
      .then((res) => { if (!cancelled && res?.success) setTokens(res.data); })
      .catch((err) => console.error('Failed to load tokens:', err))
      .finally(() => { if (!cancelled) setTokensLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId, tokensHeldStatuses, tokensHeldPage]);

  // Status filter changed — go back to page 1 rather than keep whatever
  // page number was showing for the previous filter.
  useEffect(() => { setTokensHeldPage(1); }, [tokensHeldStatuses]);

  useEffect(() => {
    if (tab !== 'Allotments' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setAllotmentsLoading(true);
    const apiFilters = Object.fromEntries(Object.entries(allotmentsFilters).filter(([, v]) => v.length > 0));
    getDashboardAllotments(loggedInUser.userId, startMs, endMs, { ...apiFilters, page: allotmentsPage })
      .then((res) => { if (!cancelled && res?.success) setAllotments(res.data); })
      .catch((err) => console.error('Failed to load allotments:', err))
      .finally(() => { if (!cancelled) setAllotmentsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId, allotmentsFilters, allotmentsPage]);

  useEffect(() => { setAllotmentsPage(1); }, [allotmentsFilters]);

  useEffect(() => {
    if (tab !== 'Agreements' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setAgreementsLoading(true);
    const apiFilters = Object.fromEntries(Object.entries(agreementsFilters).filter(([, v]) => v.length > 0));
    getDashboardAgreements(loggedInUser.userId, startMs, endMs, { ...apiFilters, page: agreementsPage })
      .then((res) => { if (!cancelled && res?.success) setAgreements(res.data); })
      .catch((err) => console.error('Failed to load agreements:', err))
      .finally(() => { if (!cancelled) setAgreementsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId, agreementsFilters, agreementsPage]);

  useEffect(() => { setAgreementsPage(1); }, [agreementsFilters]);

  useEffect(() => {
    if (tab !== 'Performance' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setPerformanceLoading(true);
    getDashboardPerformance(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setPerformance(res.data); })
      .catch((err) => console.error('Failed to load performance:', err))
      .finally(() => { if (!cancelled) setPerformanceLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId]);

  useEffect(() => {
    if (tab !== 'Persona' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setPersonaLoading(true);
    getDashboardPersona(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setPersona(res.data); })
      .catch((err) => console.error('Failed to load persona:', err))
      .finally(() => { if (!cancelled) setPersonaLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId]);

  // §8's drill-down — every card is a door. Fetches the reduced leads
  // behind whichever figure was clicked, from the same cached rows the
  // card itself was counted from (server-side), so this never disagrees
  // with the number that opened it.
  function openPersonaDrilldown(dimension, value, title) {
    if (!dimension || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    setPersonaDrilldown({ dimension, value, title });
    setPersonaDrilldownData(null);
    setPersonaDrilldownLoading(true);
    getDashboardPersonaDrilldown(loggedInUser.userId, startMs, endMs, dimension, value)
      .then((res) => { if (res?.success) setPersonaDrilldownData(res.data); })
      .catch((err) => console.error('Failed to load persona drill-down:', err))
      .finally(() => setPersonaDrilldownLoading(false));
  }

  useEffect(() => {
    if (tab !== 'Projects' || !loggedInUser?.userId) return;
    const { startMs, endMs } = periodToRange(period, fromDate, toDate);
    let cancelled = false;
    setProjectsLoading(true);
    getDashboardProjects(loggedInUser.userId, startMs, endMs)
      .then((res) => { if (!cancelled && res?.success) setProjects(res.data); })
      .catch((err) => console.error('Failed to load projects:', err))
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, period, fromDate, toDate, loggedInUser?.userId]);

  const phone = w < 720, tablet = w >= 720 && w < 1180, xl = w >= 1280;
  const cols = (n) => `repeat(${n},minmax(0,1fr))`;

  const noteCtl = (id) => {
    const open = !!notes[id];
    return {
      open,
      infoBg: open ? '#EFF4FF' : '#F7F8FA',
      infoFg: open ? '#2563EB' : '#9CA3AF',
      infoBorder: open ? '#BFDBFE' : '#EDEFF3',
      toggle: () => setNotes((s) => ({ ...s, [id]: !s[id] })),
    };
  };

  // Identity is real the instant we know who's logged in — no fetch needed.
  const M = baseMetrics();
  if (loggedInUser?.name) {
    M.name = loggedInUser.name;
    M.role = loggedInUser?.roleId?.title || '';
    M.initials = loggedInUser.name.split(' ').filter(Boolean).map((w2) => w2[0]).join('').toUpperCase();
  }
  if (overview) {
    Object.assign(M, {
      leads: overview.funnel.leads, leadsDeltaPct: overview.funnel.leadsDeltaPct,
      visits: overview.funnel.visits, visitsDeltaPct: overview.funnel.visitsDeltaPct,
      tokens: overview.funnel.tokens, tokensDeltaPct: overview.funnel.tokensDeltaPct,
      viaToken: overview.funnel.viaToken, direct: overview.funnel.direct,
      allot: overview.funnel.allot, allotDeltaPct: overview.funnel.allotDeltaPct,
      agmt: overview.funnel.agmt, agmtDeltaPct: overview.funnel.agmtDeltaPct,
      valueCr: overview.value.valueCr, valueCrDeltaPct: overview.value.valueCrDeltaPct,
      area: overview.value.area, cancels: overview.value.cancels, cancelValueCr: overview.value.cancelValueCr,
      pending: overview.outcomes.pending, ready: overview.outcomes.ready, underSanction: overview.outcomes.underSanction,
      calls: overview.calls.calls, callsDeltaPct: overview.calls.callsDeltaPct,
      answered: overview.calls.answered, talkTimeSec: overview.calls.talkTimeSec,
      projectsCount: overview.identity?.projectsCount || 0, projectChips: overview.identity?.projectChips || [],
      lastActiveMs: overview.identity?.lastActiveMs ?? null,
      name: overview.identity?.name || M.name, role: overview.identity?.role || M.role,
      initials: overview.identity?.initials || M.initials,
    });
  }

  function panels(currentTab) {
    // list rows are [label, value, sub?, color?, dimension?, dimValue?] —
    // dimension/dimValue are Persona-only (§8 drill-down); every other
    // tab's calls simply omit them, leaving the tile unclickable.
    // rows are [label, value, sub, color?, dimension?, dimValue?, badge?] —
    // badge is an optional {text,color,bg} pill shown next to the value
    // (e.g. a vs-company delta or an "x of y" ratio), see deltaBadge() below.
    const statsFn = (title, sub, list, extraBookingType) => ({
      title, sub, isStats: true, statCols: cols(phone ? 2 : xl ? Math.min(list.length, 6) : 3),
      stats: list.map(([label, value, s, color, dimension, dimValue, badge]) => ({ label, value, sub: s, color: color || '#111827', dimension, dimValue: dimValue !== undefined ? dimValue : null, badge: badge || null })),
      extraBookingType,
    });
    // Colors a vs-company (or vs-prior) delta the same way the rest of this
    // dashboard already does elsewhere (up = green, down = red, none = gray).
    const deltaBadge = (deltaPct) => {
      if (deltaPct == null) return null;
      const tone = deltaPct > 0 ? { bg: '#ECFDF5', color: '#16A34A' } : deltaPct < 0 ? { bg: '#FEF2F2', color: '#DC2626' } : { bg: '#F3F4F6', color: '#6B7280' };
      return { text: (deltaPct > 0 ? '+' : '') + deltaPct + '%', ...tone };
    };
    const ratioBadge = (count, total) => ({ text: count + ' of ' + total, bg: '#EFF6FF', color: '#2563EB' });
    // colDefs' 4th element is a desktop-tuned px width hint for that column
    // (mainly the wide first "label" column — Customer/Project/Token/etc.
    // run 150-220px there). On phone, every table scrolls horizontally
    // regardless, so keeping those desktop widths just means more scrolling
    // to reach the same data — cap each column's contribution so the whole
    // table (and the gap after that first header) is more compact on phone.
    // `filters` (optional): [{ label, columnIndex }] — renders a pill-filter
    // row above the table (desktop and phone both), options derived from the
    // table's own distinct cell values at that column. `capped` (optional):
    // true when the backend only ever sends a fixed top-N of this table's
    // real total (see each controller's TABLE_CAP) — filtering then only
    // searches within those already-fetched rows, not the true full set, so
    // DataTable shows a note making that explicit whenever a filter is active.
    // Builds a DataTable `controlled` config from {label, key} field defs —
    // `key` indexes into both the backend's filterOptions object and the
    // lifted filter-state object, `label` is what's shown in the FilterMenu.
    // Shared by Allotments and Agreements, which both filter server-side on
    // the same Project/CP/FOS(/Status) shape. Each field's state value is an
    // array of picked values (multi-select) — `applyAll` commits a whole new
    // { key: values } object in one state update (one refetch, pressed once
    // from FilterMenu's Apply button) and `reset` clears every field back to
    // empty in one update too.
    const controlledFilter = (fieldDefs, filterOptions, activeState, setActiveState, totalCount, pagination) => ({
      fields: fieldDefs.map((f) => ({ label: f.label, key: f.key, options: (filterOptions?.[f.key] || []).map((v) => ({ value: v })) })),
      active: activeState,
      applyAll: (newActive) => setActiveState((s) => ({ ...s, ...newActive })),
      reset: () => setActiveState(Object.fromEntries(fieldDefs.map((f) => [f.key, []]))),
      totalCount,
      pagination,
    });
    const tableFn = (title, sub, colDefs, rows, footer, opts) => ({
      title, sub, isTable: true, footer: footer || '',
      colsOut: colDefs.map((c) => ({ label: c[0], align: c[1] || 'left' })),
      grid: colDefs.map((c) => c[2] || 'minmax(0,1fr)').join(' '),
      minWidth: colDefs.reduce((s, c) => s + (phone ? Math.min(c[3] || 130, 100) : (c[3] || 130)), 0) + 'px',
      rows,
      filters: opts?.filters || null,
      capped: !!opts?.capped,
      controlled: opts?.controlled || null,
      loading: !!opts?.loading,
    });
    const cell = (text, align, color, weight) => ({ text, align: align || 'left', color: color || '#111827', weight: weight || '400' });
    const noteFn = (title, text, tone) => ({
      title, isNote: true, noteText: text,
      noteBg: tone === 'amber' ? '#FFFBEB' : '#F7F8FA',
      noteBorder: tone === 'amber' ? '#FDE68A' : '#EDEFF3',
      noteColor: tone === 'amber' ? '#92400E' : '#475569',
    });
    // list rows are [label, value, sub?, color?] — a per-row color (4th
    // element) wins over the function-level default, so semantically
    // different rows (a completed status vs. a cancelled one) can use the
    // same green/amber/red the stat cards already use, instead of every
    // bar defaulting to plain blue. When no color is given at all (list-wide
    // or per-row), rows cycle through CAT by position — matching the source
    // design's categorical breakdowns (status, buckets, call types). Pass an
    // explicit `color` for a SEQUENCE instead (a funnel, hour-of-day) so
    // every row stays that one color.
    // rows are [label, value, sub?, color?, dimValue?] — dimValue lets a
    // row's drill-down VALUE differ from its display label (e.g. commute
    // pairs display "home → work" but the backend matches "home -> work").
    // `dimension` (Persona-only, §8) makes every row in the list clickable.
    const barsFn = (title, sub, list, color, note, dimension) => {
      const max = Math.max(...list.map((r) => r[1]), 1);
      const total = list.reduce((s, r) => s + r[1], 0);
      return {
        title, sub, isBars: true, note: note || '', dimension,
        bars: list.map((r, i) => ({
          label: r[0], value: int(r[1]), sub: r[2] != null ? r[2] : (pctOf(r[1], total) + '% of ' + int(total)),
          width: Math.max(1.5, (r[1] / max) * 100) + '%', color: r[3] || color || CAT[i % CAT.length],
          dimValue: r[4] !== undefined ? r[4] : r[0],
        })),
      };
    };

    const timingFn = (title, sub, timing, totalBooked, note) => ({
      title, sub, isTimingRow: true, note: note || '', timing, totalBooked,
    });
    const monthlyFn = (title, sub, monthly) => ({ title, sub, isMonthly: true, monthly });

    if (currentTab === 'Performance') {
      if (!performance) return [{ isPlaceholder: true, title: 'Performance', loading: performanceLoading }];
      const pf = performance;
      if (pf.totalBooked === 0) {
        return [noteFn('No activity', 'No allotments were booked by this scope in this period.', 'amber')];
      }
      const dp = pf.dealProfile;
      const monthlySub = 'Last 12 months, one row per metric · agreements counted the month they were signed, not booked'
        + (pf.partialMonth ? ' · ' + pf.partialMonth.month + ' is partial — 1 to ' + pf.partialMonth.throughDay : '');
      const mixItems = [
        ...(pf.bySource.length ? [{ title: 'By source', sub: 'R&L and Online fold into OP', items: pf.bySource.map((s) => ({ label: s.source, count: s.count, pct: s.pct })) }] : []),
        ...(pf.byProject.length ? [{ title: 'By project', sub: 'This period’s bookings', items: pf.byProject.map((p) => ({ label: p.name, count: p.count, pct: p.pct })) }] : []),
        ...(pf.byUnitType.length ? [{ title: 'By unit type', sub: 'Free-text, folded into families · ' + pf.unitTypeCoveragePct + '% recognised', items: pf.byUnitType.map((u) => ({ label: u.type, count: u.count, pct: u.pct })) }] : []),
      ];
      return [
        timingFn('Conversion timing', 'How long each step takes, against the company', pf.timing, pf.totalBooked,
          'Company figures run the identical calculation over every booking company-wide in this period, not a cached benchmark. “Booking → agreement” is measured only over deals that have actually signed — most haven’t yet, so its sample is usually small.'),
        monthlyFn('Month by month', monthlySub, pf.monthly),
        ...(mixItems.length ? [{ isDonutRow: true, rowItems: mixItems }] : []),
        statsFn('Deal profile', 'What these deals look like, against the company', [
          ['Avg ticket', '₹' + int(dp.avgTicketL) + ' L', dp.avgTicketDeltaPct == null ? 'no company benchmark' : 'company ₹' + int(dp.companyAvgTicketL) + ' L', null, null, null, deltaBadge(dp.avgTicketDeltaPct)],
          ['Avg unit size', sqft(dp.avgAreaSqft), dp.avgAreaDeltaPct == null ? 'no company benchmark' : 'company ' + sqft(dp.companyAvgAreaSqft), null, null, null, deltaBadge(dp.avgAreaDeltaPct)],
          ['Total area', sqft(dp.totalAreaSqft), 'across ' + units(pf.totalBooked)],
          ['Peak day', dp.peakDay?.day || '—', dp.peakDay ? 'bookings by weekday' : '', null, null, null, dp.peakDay ? ratioBadge(dp.peakDay.count, pf.totalBooked) : null],
          ['Weekend split', dp.weekendCount + ' / ' + dp.weekdayCount, 'weekend / weekday'],
        ], pf.bookingType.length ? pf.bookingType : null),
      ];
    }

    if (currentTab === 'Persona') {
      if (!persona) return [{ isPlaceholder: true, title: 'Persona', loading: personaLoading }];
      const ps = persona;
      // Phases 1–2 of SALES_360_PERSONA_DATA_LOGIC_SPEC.md's own build order
      // (§12): coverage, age band, income band, gender, occupation — pure
      // JSON reads, no address logic yet. Home/work locality, state/
      // language, tags, employer, priority score and the drill-down all
      // follow in later phases — this tab intentionally shows only what's
      // been built so far rather than a placeholder implying nothing is
      // planned.
      if (!ps.persona) {
        return [noteFn('Persona isn’t available for this scope right now',
          'This is an enrichment lookup, not a source of truth, so a lookup problem never blocks the rest of the dashboard — it just means this tab has nothing to show this time. ' + int(ps.leads) + ' leads in this period either way.', 'amber')];
      }
      const cov = ps.persona.coverage;
      if (cov.leads === 0) {
        return [noteFn('No leads in this period', 'This scope has no leads in the selected period, so there’s nothing to profile.', 'amber')];
      }
      if (cov.enriched === 0) {
        return [noteFn('None of this scope’s leads carry a background profile',
          'None of these ' + int(cov.leads) + ' leads in this period carry a background profile. Enrichment only runs on-demand and only reaches a small, recent slice of leads — a manager having none profiled is normal, not a fault. It never runs retroactively on older leads.', 'amber')];
      }
      const lakh = (v) => (v == null ? '—' : '₹' + (v / 100000).toFixed(1) + ' L');
      return [
        statsFn('Coverage', 'A paid bureau background check, run on-demand on a small, recent slice of leads — never a source of truth. Click a figure to see the leads behind it.', [
          ['Leads this period', int(cov.leads), 'the denominator — every lead, profiled or not'],
          ['Profiled', int(cov.enriched), cov.pct + '% of leads this period', null, 'profiled', null],
          ['Median income', lakh(ps.persona.medianIncome), 'approximated from band mid-points'],
          ['High priority', int(ps.persona.highPriority), 'score ≥ 70 — not a pure persona figure, it mixes in booking stage', null, 'priority', null],
        ]),
        ...(ps.persona.ageBand.length ? [barsFn('Age band', 'Natural order, not ranked by size', ps.persona.ageBand.map((b) => [b.label, b.n]), CAT[2], '', 'age')] : []),
        ...(ps.persona.incomeBand.length
          ? [barsFn('Income band', 'Annual, self-reported to the bureau — the thinnest field: on record for ' + int(ps.persona.incomeKnown) + ' of ' + int(cov.enriched) + ' profiled leads', ps.persona.incomeBand.map((b) => [b.label, b.n]), CAT[5], '', 'income')]
          : [statsFn('Income band', 'Annual, self-reported to the bureau', [['On record', '0 of ' + int(cov.enriched), 'no income on record for any of these profiled leads', '#9CA3AF']])]),
        ...(ps.persona.gender.length ? [barsFn('Gender', 'As recorded by the bureau', ps.persona.gender.map((b) => [b.label, b.n]), CAT[0])] : []),
        barsFn('Occupation', 'Stated to the bureau — on record for ' + int(ps.persona.occupationKnown) + ' of ' + int(cov.enriched) + ' profiled leads. "Service (inferred…)" is our own read of an office address, never something the bureau stated.',
          ps.persona.occupation.map((b) => [b.label, b.n]), CAT[4], '', 'occupation'),
        statsFn('Where they live', 'Any Pune pincode across a lead’s addresses, not just the primary one. Click a figure to see the leads behind it.', [
          ['In Pune', int(ps.persona.origins.inPune), ps.persona.origins.known ? pctOf(ps.persona.origins.inPune, ps.persona.origins.known) + '% of ' + int(ps.persona.origins.known) + ' with a usable address' : '', null, 'inpune', null],
          ['Outside Pune', int(ps.persona.origins.outsidePune), '', null, 'outsidepune', null],
          ['No usable address', int(ps.persona.origins.noAddress), 'no address with a pincode this app recognises'],
        ]),
        ...(ps.persona.origins.areas.length
          ? [barsFn('Pune localities', 'The strongest address claim per lead — owned, then primary, then permanent, then untyped',
            ps.persona.origins.areas.map((a) => [a.label, a.n]), CAT[3],
            (ps.persona.origins.otherAreas ? int(ps.persona.origins.otherAreas) + ' more live in smaller Pune localities not listed above. ' : '')
            + (ps.persona.origins.puneUnmapped ? int(ps.persona.origins.puneUnmapped) + ' live in a Pune pincode not yet on this app’s locality list — only 5 localities are mapped so far.' : ''),
            'locality')]
          : []),
        ...(ps.persona.states.length ? [barsFn('State', 'The permanent address if there is one, else any address with a resolvable state', ps.persona.states.map((s) => [s.label, s.n]), CAT[1], '', 'state')] : []),
        ...(ps.persona.languages.length ? [barsFn('Likely language', 'A likelihood to open a conversation with — never a fact about the person. Read off the state above, never stored or asked.', ps.persona.languages.map((l) => [l.label, l.n]), CAT[5], '', 'language')] : []),
        ...(ps.persona.workLocality.length ? [barsFn('Where they work', 'Latest-dated address classified as an office · ' + int(ps.persona.workTotal) + ' of ' + int(cov.enriched) + ' profiled leads have one',
          ps.persona.workLocality.map((w) => [w.label, w.n]), CAT[3],
          ps.persona.workOther ? int(ps.persona.workOther) + ' more work in localities not listed above.' : '', 'worklocality')] : []),
        ...(ps.persona.commute.withBoth
          ? [barsFn('Commute', ps.persona.commute.sameArea + ' of ' + ps.persona.commute.withBoth + ' with both a home and work area live and work in the same one — reported first, not filtered out',
            [
              ['Live and work in the same area', ps.persona.commute.sameArea, null, '#16A34A', null],
              ...ps.persona.commute.pairs.map((p) => [p.home + ' → ' + p.work, p.n, null, null, p.home + ' -> ' + p.work]),
            ],
            null,
            (ps.persona.commute.commuters - ps.persona.commute.pairsShown) > 0
              ? int(ps.persona.commute.commuters - ps.persona.commute.pairsShown) + ' more travel less common routes not listed above.'
              : '',
            'commute')]
          : []),
        ...(ps.persona.tags.length ? [barsFn('Tags', 'A lead can carry several — these don’t sum to ' + int(cov.enriched), ps.persona.tags.map((t) => [t.label, t.n]), CAT[4], '', 'tag')] : []),
      ];
    }

    if (currentTab === 'Projects') {
      if (!projects) return [{ isPlaceholder: true, title: 'Projects', loading: projectsLoading }];
      const pj = projects;
      if (pj.totalAllot === 0) {
        return [noteFn('No activity', 'No allotments were booked by this scope in this period.', 'amber')];
      }
      const topAllot = pj.rows[0]?.allot || 0;
      const top2Allot = (pj.rows[0]?.allot || 0) + (pj.rows[1]?.allot || 0);
      return [
        {
          title: 'Project details',
          sub: 'Every project, not a top three · Leads/Calls/Tokens are derived from each lead’s earliest site visit — a real but approximate signal, since no direct field links any of the three to a project. Allotments and Agreement Done use the exact project link.',
          isProjectDetails: true, detailRows: pj.detailRows, detailTotals: pj.detailTotals,
          onOpenProject: (p) => { setOpenProject(p); setProjectStatusFilter('All'); },
        },
        tableFn('Value by project', 'Allotments, registrations and value',
          [['Project', 'left', 'minmax(0,1.8fr)', 220], ['Allotments', 'right'], ['Agreements', 'right'], ['Cancelled', 'right'], ['Sales value', 'right']],
          pj.rows.map((r) => ({ cells: [
            cell(r.name, 'left', '#111827', '600'),
            cell(int(r.allot), 'right', '#111827', '600'),
            cell(r.registered ? int(r.registered) : '—', 'right', r.registered ? '#16A34A' : '#9CA3AF'),
            cell(r.cancelled ? int(r.cancelled) : '—', 'right', r.cancelled ? '#DC2626' : '#9CA3AF'),
            cell(cr(r.valueCr), 'right', '#111827', '600'),
          ] })),
          'Top project holds ' + pctOf(topAllot, pj.totalAllot) + '% of allotments, the top two ' + pctOf(top2Allot, pj.totalAllot) + '%',
          { filters: [{ label: 'Project', columnIndex: 0 }] }),
        ...(pj.topCPs.length ? [barsFn('Top channel partners', 'Of partner-sourced allotments', pj.topCPs.map((c) => [c.label, c.count]), '#0D9488',
          'A channel partner is recorded on ' + pj.viaCPPct + '% of this scope’s allotments — the rest are Direct or OP walk-ins with no partner.')] : []),
        ...(pj.topFOS.length ? [barsFn('Top FOS', 'The FOS names its CP — the same partnership often holds both', pj.topFOS.map((f) => [f.label, f.count]), '#6366F1')] : []),
      ];
    }

    if (currentTab === 'Leads & Calls') {
      if (!leadsCalls) return [{ isPlaceholder: true, title: 'Leads & Calls', loading: leadsCallsLoading }];
      const lc = leadsCalls;
      if (!lc.hasData) {
        return [noteFn('No activity', 'No leads were assigned to this scope in this period, so there’s nothing to show yet.', 'amber')];
      }
      return [
        statsFn('Calls', 'Answered means callStatus = answered, never the connected flag', [
          ['Leads', int(lc.leads), 'assigned this period'],
          ['Calls made', int(lc.calls), int(lc.calledLeadsCount) + ' unique leads called'],
          ['Calls a day', lc.callsPerDay.toFixed(1), 'over ' + lc.activeDays + ' days they called', '#2563EB'],
          ['Answered', int(lc.answered), lc.answeredPct + '% of calls made'],
          ['Talk time', lc.talkTimeHours.toFixed(1) + 'h', lc.avgTalkSec + 's a call'],
          ['Last activity', lc.lastActiveMs != null ? formatLastActive(lc.lastActiveMs) : '—', 'most recent logged action'],
        ]),
        barsFn('Reach · calls on their leads', 'Scoped to both sides — their calls, their leads', [
          ['Spoken to', lc.reach.spokenTo, pctOf(lc.reach.spokenTo, lc.reach.leads) + '% of leads'],
          ['Called, not yet spoken to', Math.max(0, lc.reach.called - lc.reach.spokenTo), pctOf(Math.max(0, lc.reach.called - lc.reach.spokenTo), lc.reach.leads) + '% of leads'],
          ['Never called', Math.max(0, lc.reach.leads - lc.reach.called), pctOf(Math.max(0, lc.reach.leads - lc.reach.called), lc.reach.leads) + '% of leads'],
        ], '#2563EB', (Math.max(0, lc.reach.leads - lc.reach.spokenTo)) + ' of ' + lc.reach.leads + ' leads have not been spoken to. Counting every call on their leads would credit them with other people’s work.'),
        barsFn('How far the leads got', 'The leads assigned in this period, followed forward', [
          ['Leads assigned', lc.funnel.leads, '100%'],
          ['Site visited', lc.funnel.visited, pctOf(lc.funnel.visited, lc.funnel.leads) + '% of leads'],
          ['Took a token', lc.funnel.tokened, pctOf(lc.funnel.tokened, lc.funnel.leads) + '% of leads'],
          ['Allotted', lc.funnel.allotted, pctOf(lc.funnel.allotted, lc.funnel.leads) + '% of leads'],
        ], '#2563EB'),
        ...(lc.leadTemperature.length
          ? [barsFn('Lead temperature', 'AI lead temperature — based on the ' + lc.aiCoveragePct + '% of calls with AI analysis', lc.leadTemperature.map((t) => [t.label, t.count]))]
          : []),
        ...(lc.callType.length
          ? [barsFn('What the calls were about', 'AI call type, folded into readable groups — based on the ' + lc.aiCoveragePct + '% of calls with AI analysis', lc.callType.map((t) => [t.label, t.count]), null, 'Grouped by keyword from the AI’s own free-text summary of each call — not an exact category, but close enough to read at a glance.')]
          : []),
        barsFn('Best calling hour', 'Call volume and answer rate by time of day', lc.byHour.map((h) => [h.label, h.calls, h.answeredPct + '% answered']), '#2563EB'),
      ];
    }

    if (currentTab === 'Tokens') {
      if (!tokens) return [{ isPlaceholder: true, title: 'Tokens', loading: tokensLoading }];
      const tk = tokens;
      if (tk.tokens === 0) {
        return [noteFn('No activity', 'No tokens were taken by this scope in this period.', 'amber')];
      }
      const STATUS_LABEL = { allotted: 'Allotted', active: 'Active', notConfirmed: 'Not confirmed', refunded: 'Refunded' };
      return [
        statsFn('Tokens', 'Event and preselect routes only — a sustenance booking never has a token', [
          ['Tokens', int(tk.tokens), tk.allottedPct + '% allotted'],
          ['Allotted', int(tk.allotted), tk.allottedPct + '% of tokens', '#16A34A'],
          ['Active', int(tk.active), 'no allotment yet'],
          ['Not confirmed', int(tk.notConfirmed), 'expired, cancelled or refund-rejected', '#D97706'],
          ['Refunded', int(tk.refunded), 'returned to the customer', '#DC2626'],
          ['Token value held', '₹' + int(tk.valueHeldL) + ' L', 'active + not-confirmed tokens'],
        ]),
        barsFn('The four buckets', 'Every token sits in exactly one — they sum to ' + int(tk.tokens), [
          ['Allotted', tk.allotted], ['Active', tk.active], ['Not confirmed', tk.notConfirmed], ['Refunded', tk.refunded],
        ], null, 'A token counts as allotted only when a real allotment exists against the same lead — never guessed from its own status text.'),
        tableFn('Tokens held longest without an allotment', 'Oldest first',
          [['Token', 'left', 'minmax(0,1fr)', 150], ['Customer', 'left', 'minmax(0,1.3fr)', 180], ['Amount', 'right'], ['Days held', 'right'], ['Status', 'left']],
          tk.heldLongest.map((h) => ({ cells: [
            cell(h.tokenNumber, 'left', '#6B7280'),
            cell(h.customerName || h.leadDisplayId || '—', 'left', '#111827', '600'),
            cell('₹' + int(h.amount), 'right', '#111827', '600'),
            cell(h.daysHeld + 'd', 'right', '#D97706', '600'),
            cell(STATUS_LABEL[h.status] || h.status, 'left'),
          ] })),
          'Showing ' + tk.heldLongest.length + ' oldest of ' + int(tk.heldLongestTotal ?? (tk.active + tk.notConfirmed)) + ' still open',
          { capped: true, controlled: {
            fields: [{ label: 'Status', key: 'status', options: (tk.heldFilterOptions?.status || []).map((v) => ({ value: v })) }],
            active: { status: tokensHeldStatuses },
            applyAll: (newActive) => setTokensHeldStatuses(newActive.status || []),
            reset: () => setTokensHeldStatuses([]),
            totalCount: tk.heldLongestTotal,
            pagination: { page: tk.heldPage, totalPages: tk.heldTotalPages, onPageChange: setTokensHeldPage },
          }, loading: tokensLoading }),
        tableFn('By event', 'Tokens are an event-route stage', [['Event', 'left', 'minmax(0,1.6fr)', 220], ['Status', 'left'], ['Tokens', 'right'], ['Allotted', 'right'], ['Token → allot', 'right']],
          tk.byEvent.map((e) => ({ cells: [
            cell(e.title, 'left', '#111827', '600'),
            cell(e.status || '—', 'left', e.status === 'Live' ? '#16A34A' : '#9CA3AF', '600'),
            cell(int(e.tokens), 'right'),
            cell(int(e.allotted), 'right', '#111827', '600'),
            cell(pctOf(e.allotted, e.tokens) + '%', 'right'),
          ] })), '', { filters: [{ label: 'Status', columnIndex: 1 }] }),
      ];
    }

    if (currentTab === 'Allotments') {
      if (!allotments) return [{ isPlaceholder: true, title: 'Allotments', loading: allotmentsLoading }];
      const am = allotments;
      if (am.allot === 0) {
        return [noteFn('No activity', 'No allotments were booked by this scope in this period.', 'amber')];
      }
      return [
        statsFn('Allotments', 'Sales value is BSP — the total key adds stamp duty and GST', [
          ['Allotments', int(am.allot), sqft(am.area)],
          ['Sales value', cr(am.valueCr), 'BSP'],
          ['Avg ticket', '₹' + int(am.avgTicketL) + ' L', 'per allotment'],
          ['Registered', int(am.registered), 'agreement done', '#16A34A'],
          ['Cancelled', int(am.cancels), 'own status list', '#DC2626'],
        ]),
        barsFn('Status', 'Cancellations are their own list — never subtracted from allotments', am.statusBreakdown.map((s) => [s.status, s.count]), null, 'The statuses above sum to ' + int(am.allot) + ' allotments.'),
        tableFn('Allotments', 'Most recent first',
          [['Customer', 'left', 'minmax(0,1.2fr)', 160], ['Project', 'left', 'minmax(0,1.2fr)', 150], ['Unit', 'left'], ['BSP', 'right'], ['CP', 'left', 'minmax(0,1.2fr)', 160], ['FOS', 'left'], ['Status', 'left', 'minmax(0,1.2fr)', 170]],
          am.rows.map((r) => ({ cells: [
            cell(r.customerName || r.leadDisplayId || '—', 'left', '#111827', '600'),
            cell(r.projectName, 'left', '#6B7280'),
            cell(r.unitNo || '—', 'left', '#6B7280'),
            cell('₹' + int(r.bspL) + ' L', 'right', '#111827', '600'),
            cell(r.cpName || '—', 'left', '#6B7280'),
            cell(r.fosName || '—', 'left', '#6B7280'),
            cell(r.status + (r.isRegistered ? ' · Registered' : ''), 'left', r.isRegistered ? '#16A34A' : '#111827'),
          ] })),
          'Showing ' + am.rows.length + ' of ' + int(am.totalRows) + ' · no customer phone numbers are shown',
          { capped: true, controlled: controlledFilter(
            [{ label: 'Project', key: 'project' }, { label: 'CP', key: 'cp' }, { label: 'FOS', key: 'fos' }, { label: 'Status', key: 'status' }],
            am.filterOptions, allotmentsFilters, setAllotmentsFilters, am.totalRows,
            { page: am.page, totalPages: am.totalPages, onPageChange: setAllotmentsPage },
          ), loading: allotmentsLoading }),
      ];
    }

    if (currentTab === 'Agreements') {
      if (!agreements) return [{ isPlaceholder: true, title: 'Agreements', loading: agreementsLoading }];
      const ag = agreements;
      if (ag.totalBooked === 0) {
        return [noteFn('No activity', 'No allotments were booked by this scope in this period.', 'amber')];
      }
      return [
        statsFn('Agreements', 'Registered = a real agreement form with a signed date, not this period’s bookings alone', [
          ['Registered', int(ag.agreements), ag.registeredPct + '% of ' + int(ag.totalBooked) + ' booked', '#16A34A'],
          ['Value', cr(ag.valueCr), 'BSP, registered only'],
          ['Median turnaround', ag.medianDaysToRegister != null ? ag.medianDaysToRegister + 'd' : '—', 'booking to signing'],
          ['Pending', int(ag.pending), 'booked, not yet registered', '#D97706'],
          ['Value at risk', cr(ag.pendingValueCr), 'BSP, still pending'],
        ]),
        ...(ag.agingBuckets.length
          ? [barsFn('Pending, by age', 'How long since booking, for allotments still without a registered agreement', ag.agingBuckets.map((b) => [b.bucket, b.count]), null, 'Buckets sum to ' + int(ag.pending) + ' pending allotments.')]
          : []),
        ...(ag.byProject.length
          ? [barsFn('Registered, by project', 'Where the ' + int(ag.agreements) + ' registered agreements sit', ag.byProject.map((p) => [p.name, p.count]))]
          : []),
        tableFn('Oldest pending agreements', 'Longest since booking first',
          [['Customer', 'left', 'minmax(0,1.2fr)', 160], ['Project', 'left', 'minmax(0,1.2fr)', 150], ['Unit', 'left'], ['BSP', 'right'], ['CP', 'left', 'minmax(0,1.2fr)', 160], ['FOS', 'left'], ['Days pending', 'right']],
          ag.rows.map((r) => ({ cells: [
            cell(r.customerName || r.leadDisplayId || '—', 'left', '#111827', '600'),
            cell(r.projectName, 'left', '#6B7280'),
            cell(r.unitNo || '—', 'left', '#6B7280'),
            cell('₹' + int(r.bspL) + ' L', 'right', '#111827', '600'),
            cell(r.cpName || '—', 'left', '#6B7280'),
            cell(r.fosName || '—', 'left', '#6B7280'),
            cell(r.daysPending + 'd', 'right', r.daysPending > 90 ? '#DC2626' : '#111827', '600'),
          ] })),
          'Showing ' + ag.rows.length + ' of ' + int(ag.totalPendingRows) + ' pending',
          { capped: true, controlled: controlledFilter(
            [{ label: 'Project', key: 'project' }, { label: 'CP', key: 'cp' }, { label: 'FOS', key: 'fos' }],
            ag.filterOptions, agreementsFilters, setAgreementsFilters, ag.totalPendingRows,
            { page: ag.page, totalPages: ag.totalPages, onPageChange: setAgreementsPage },
          ), loading: agreementsLoading }),
      ];
    }

    if (currentTab === 'Site Visits') {
      if (siteVisits) {
        const sv = siteVisits;
        return [
          statsFn('Site visits', 'This period', [
            ['Site visits', int(sv.visits), int(sv.uniqueLeads) + ' unique leads'],
            ['Avg a day', sv.visitsPerDay.toFixed(1), 'over ' + sv.periodDays + ' days in the period'],
            ['Revisit rate', sv.revisitRatePct + '%', int(sv.revisitedLeadsCount) + ' leads visited twice'],
            ['Visit → allotment', sv.visitToAllotPct + '%', int(sv.allot) + ' of ' + int(sv.visits)],
            ['Conducted by others', sv.conductedByOthersPct + '%', 'visit logged by another user'],
            ['Median to booking', sv.medianDaysToBooking != null ? sv.medianDaysToBooking + 'd' : '—', 'company ' + (sv.companyMedianDaysToBooking != null ? sv.companyMedianDaysToBooking + 'd' : '—'), '#16A34A'],
          ]),
          tableFn('By project', 'Ranked by visits', [['Project', 'left', 'minmax(0,1.6fr)', 220], ['Visits', 'right'], ['Allotments', 'right'], ['Visit → allot', 'right']],
            sv.byProject.map((p) => ({ cells: [cell(p.name), cell(int(p.visits), 'right'), cell(int(p.allot), 'right', '#111827', '600'), cell(pctOf(p.allot, p.visits) + '%', 'right')] }))),
          tableFn('By source', 'OP absorbs R&L and Online', [['Source', 'left', 'minmax(0,1.6fr)', 220], ['Visits', 'right'], ['Allotments', 'right'], ['Conversion', 'right']],
            sv.bySource.map((s) => ({ cells: [cell(s.source), cell(int(s.visits), 'right'), cell(int(s.allot), 'right', '#111827', '600'), cell(pctOf(s.allot, s.visits) + '%', 'right')] }))),
        ];
      }
      return [{ isPlaceholder: true, title: 'Site visits', loading: siteVisitsLoading }];
    }

    return [{ isPlaceholder: true, title: currentTab }];
  }

  // ---- derived layout values (breakpoints) ----
  const gridFour = cols(phone ? 2 : tablet ? 2 : 4);
  const pagePad = phone ? '20px 20px 48px' : '20px 24px 56px';
  const stickyPad = phone ? '10px 14px 10px' : '16px 24px 18px';
  const identityDir = w < 1024 ? 'column' : 'row';
  const identityAlign = w < 1024 ? 'flex-start' : 'baseline';
  // NOTE: the fork/connector orientation in the source design is hardcoded to
  // 'row' in the final revision — it does NOT reorient to a vertical layout
  // on phone widths, despite the written design spec describing that intent.
  // Ported faithfully as coded.
  const forkDir = 'row';
  const forkCols = 'minmax(0,1fr) 44px minmax(0,1fr)';
  const bandConnW = '46px';

  const range = periodToRange(period, fromDate, toDate);
  const periodDaysCount = Math.max(1, Math.round((range.endMs - range.startMs) / 86400000));

  const days = overview?.dailyActivity || [];
  // All three series share one scale, so it has to be the max across all
  // three — using calls alone collapsed to 1 whenever calls were 0 (a real
  // sandbox gap: CallLogs.userId matches no real Users row here), which
  // blew up bookings/visits bars to thousands of pixels tall on any day
  // with real activity, overflowing straight through the chart container.
  const maxV = Math.max(...days.map((d) => Math.max(d.calls, d.visits, d.bookings)), 1);
  const H = 150;
  const daysOut = days.map((d) => {
    const dow = dowOf(d.date);
    const weekend = dow === 0 || dow === 6;
    return {
      dayNum: Number(d.date.slice(-2)), date: d.date, labelColor: weekend ? '#6B7280' : '#9CA3AF', colBg: weekend ? '#F1F3F7' : 'transparent',
      hCalls: Math.max(2, Math.round((d.calls / maxV) * H)), hVisits: Math.max(2, Math.round((d.visits / maxV) * H)), hBookings: Math.max(2, Math.round((d.bookings / maxV) * H)),
      enter: () => setHover(d), leave: () => setHover(null),
    };
  });
  const hovered = hover ? {
    dateLabel: new Date(hover.date + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
    calls: hover.calls, visits: hover.visits, bookings: hover.bookings,
  } : null;
  const chartRead = summarizeDailyActivity(overview?.dailyActivity);
  const reachLine = overview ? int(M.calls) + ' calls and ' + int(M.visits) + ' site visits logged this period.' : '';

  const nowIst = nowIstParts();

  const subTabs = TABS.map((label) => {
    const on = tab === label;
    const count = { 'Leads & Calls': M.leads, Persona: persona?.persona?.coverage?.enriched, 'Site Visits': M.visits, Tokens: M.tokens, Allotments: M.allot, Agreements: M.agmt }[label];
    const icon = {
      Overview: 'layout-dashboard', Performance: 'trending-up', 'Leads & Calls': 'phone',
      Persona: 'users', 'Site Visits': 'map-pin', Tokens: 'ticket',
      Allotments: 'file-check', Agreements: 'circle-check', Projects: 'layers',
    }[label];
    return {
      label, icon,
      divider: label === 'Leads & Calls' || label === 'Projects',
      count: count === undefined ? null : int(count),
      bg: on ? '#2563EB' : 'transparent', fg: on ? '#FFFFFF' : '#6B7280',
      weight: on ? '600' : '500', iconBg: on ? '#FFFFFF' : '#9CA3AF',
      shadow: on ? '0 1px 2px rgba(37,99,235,.35)' : 'none',
      countBg: on ? 'rgba(255,255,255,.22)' : '#F1F3F7', countFg: on ? '#FFFFFF' : '#6B7280',
      hoverable: !on,
      select: () => setTab(label),
    };
  });

  const isOverview = tab === 'Overview';
  const otherPanels = panels(tab).map((p, i) => {
    if (p.isPlaceholder || p.isDonutRow) return p;
    const c = noteCtl(tab + '#' + i);
    return { ...p, isNoteOnly: !!p.isNote, isCard: !p.isNote, hasNote: !!p.note, showNoteBox: !!p.isNote && c.open, showBarNote: !!p.note && c.open, ...c };
  });

  const identity = {
    initials: M.initials, name: M.name, role: M.role, status: 'Active',
    scope: overview
      ? int(M.projectsCount) + (M.projectsCount === 1 ? ' project' : ' projects') + ' · ' + formatLastActive(M.lastActiveMs)
      : 'Loading…',
  };

  const heroFigures = [
    { label: 'Sold', value: cr(M.valueCr), color: '#111827', sub: units(M.allot) + ' · ' + sqft(M.area) },
    { label: 'Cancelled', value: cr(M.cancelValueCr), color: '#DC2626', sub: units(M.cancels) },
    { label: 'Registered', value: int(M.agmt), color: '#16A34A', sub: 'of ' + int(M.allot) + ' allotments' },
  ];

  const isCustom = period === 'Custom';
  const periods = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom'].map((label) => ({
    label, bg: label === period ? '#FFFFFF' : 'transparent', fg: label === period ? '#111827' : '#6B7280',
    shadow: label === period ? '0 1px 2px rgba(16,24,40,.08)' : 'none',
    select: () => setPeriod(label),
  }));
  // Whichever tab is actually on screen right now — used to show a small,
  // non-blocking spinner instead of the old full-page loader (removed
  // earlier) or a full-tab placeholder swap. Data isn't cleared to null
  // between fetches anymore (see each effect above), so the last-loaded
  // numbers stay visible while this spins, rather than the whole panel
  // blanking out on every filter/tab change.
  const currentTabLoading = tab === 'Overview' ? overviewLoading
    : tab === 'Performance' ? performanceLoading
    : tab === 'Leads & Calls' ? leadsCallsLoading
    : tab === 'Persona' ? personaLoading
    : tab === 'Site Visits' ? siteVisitsLoading
    : tab === 'Tokens' ? tokensLoading
    : tab === 'Allotments' ? allotmentsLoading
    : tab === 'Agreements' ? agreementsLoading
    : tab === 'Projects' ? projectsLoading
    : false;
  const periodLabel = (period === 'Custom' ? fromDate + ' → ' + toDate
    : period === 'Today' ? fmtShort(range.startMs) + ' ' + fmtYear(range.startMs)
    : fmtShort(range.startMs) + ' – ' + fmtShort(range.endMs) + ' ' + fmtYear(range.endMs));
  const periodTitle = period === 'This Month' ? 'This month' : period;

  const todayStrip = (overview?.today ? [
    ['Calls', int(overview.today.calls), 'avg ' + Math.round(M.calls / periodDaysCount)],
    ['Talk time', Math.round(overview.today.talkTimeSec / 60) + 'm', 'avg ' + Math.round(M.talkTimeSec / periodDaysCount / 60) + 'm'],
    ['Site visits', int(overview.today.visits), 'avg ' + Math.round(M.visits / periodDaysCount)],
    ['New leads', int(overview.today.leads), 'avg ' + Math.round(M.leads / periodDaysCount)],
  ] : [
    ['Calls', '—', ''], ['Talk time', '—', ''], ['Site visits', '—', ''], ['New leads', '—', ''],
  ]).map(([label, value, sub]) => ({ label, value, sub, color: value === '—' ? '#9CA3AF' : '#111827' }));

  const s1Delta = fmtDelta(M.leadsDeltaPct);
  const s1 = {
    label: 'Leads', value: int(M.leads), sub: int(M.visits) + ' site visits',
    color: '#EC4899', tint: '#FDF2F8', border: '#FBCFE8', icon: 'user-plus',
    pct: pctOf(M.visits, M.leads) + '%', pctNote: 'visited', connFrom: '#EC4899', connTo: '#F97316',
    delta: s1Delta.text, deltaColor: s1Delta.color,
  };
  const s2Delta = fmtDelta(M.visitsDeltaPct);
  const s2 = {
    label: 'Site visits', value: int(M.visits), sub: '',
    color: '#F97316', tint: '#FFF7ED', border: '#FED7AA', icon: 'map-pin',
    delta: s2Delta.text, deltaColor: s2Delta.color,
  };
  const monthFigures = [
    { label: 'Calls', value: int(M.calls), ...fmtDelta(M.callsDeltaPct) },
    { label: 'Sales value', value: cr(M.valueCr), ...fmtDelta(M.valueCrDeltaPct) },
  ];
  const lanes = {
    tokenBg: '#ECFEFF', tokenBorder: '#A5F3FC', tokenTag: '#0891B2', tokenTagBg: '#CFFAFE',
    sustBg: '#F0FDF4', sustBorder: '#BBF7D0', sustTag: '#15803D', sustTagBg: '#DCFCE7',
  };
  const forkTokenDelta = fmtDelta(M.tokensDeltaPct);
  const fork = {
    tokens: int(M.tokens), tokenNote: pctOf(M.tokens, M.visits) + '% of visits',
    tokenPct: pctOf(M.viaToken, M.tokens) + '%', viaToken: int(M.viaToken),
    directPct: pctOf(M.direct, M.visits) + '%', direct: int(M.direct),
    tokenDelta: forkTokenDelta.text, tokenDeltaColor: forkTokenDelta.color,
    tokenColor: '#06B6D4', allotColor: '#2563EB', directColor: '#16A34A',
  };
  const tailAllotDelta = fmtDelta(M.allotDeltaPct);
  const tailAgmtDelta = fmtDelta(M.agmtDeltaPct);
  const tail = {
    allotments: int(M.allot), allotSub: cr(M.valueCr),
    agmtPct: pctOf(M.agmt, M.allot) + '%', agreements: int(M.agmt),
    allotColor: '#2563EB', allotTint: '#EFF4FF', allotBorder: '#BFDBFE', allotIcon: 'file-check',
    agmtColor: '#16A34A', agmtTint: '#ECFDF5', agmtBorder: '#A7F3D0', agmtIcon: 'circle-check',
    allotDelta: tailAllotDelta.text, allotDeltaColor: tailAllotDelta.color,
    agmtDelta: tailAgmtDelta.text, agmtDeltaColor: tailAgmtDelta.color,
  };
  const outcomes = [
    { label: 'Cancelled', value: int(M.cancels), color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', sub: cr(M.cancelValueCr) + ' · leaves after allotment' },
    { label: 'Awaiting agreement', value: int(M.pending), color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', sub: 'allotted, not yet registered' },
    { label: 'Ready to register', value: int(M.ready), color: '#16A34A', bg: '#ECFDF5', border: '#A7F3D0', sub: 'unit sanctioned · chaseable today' },
    { label: 'Under sanction', value: int(M.underSanction), color: '#475569', bg: '#F7F8FA', border: '#EDEFF3', sub: 'on Hold · no ageing shown' },
  ];

  const series = [{ label: 'Calls', color: '#8B5CF6' }, { label: 'Site visits', color: '#F97316' }, { label: 'Bookings', color: '#2563EB' }];

  const pageTitle = (M.name ? M.name.split(' ')[0] : 'Your') + '’s dashboard · ' + tab;

  // Title + period-filter row, pulled out into a variable so it can be
  // placed in two different spots depending on breakpoint (see return
  // below) without duplicating the JSX.
  const titleBar = (
    <div style={{ background: '#FFFFFFF2', borderBottom: '1px solid #EDEFF3', backdropFilter: 'blur(8px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px 20px', flexWrap: 'wrap', padding: '12px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{pageTitle}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{periodLabel}</div>
            {/* Small, non-blocking spinner for whichever tab is actually
                refetching — replaces the removed full-page loader and the
                old full-tab "Loading…" placeholder swap. Only shows for the
                tab currently on screen, never the whole app. */}
            {(overviewLoading || currentTabLoading) && (
              <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #E5E7EB', borderTopColor: '#2563EB', animation: 'vjSpin 0.7s linear infinite', flex: '0 0 auto' }} />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2, padding: 3, background: '#F1F3F7', borderRadius: 9 }}>
            {periods.map((p) => (
              <div key={p.label} onClick={p.select} style={{ padding: '6px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', background: p.bg, color: p.fg, boxShadow: p.shadow }}>{p.label}</div>
            ))}
          </div>
          {isCustom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: '7px 9px', border: '1px solid #EDEFF3', background: '#FFFFFF', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit', color: '#111827' }} />
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>to</div>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: '7px 9px', border: '1px solid #EDEFF3', background: '#FFFFFF', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit', color: '#111827' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // overflowX: 'clip' below (phone only) is a hard backstop against any
  // descendant forcing the page wider than the viewport — 'clip' (unlike
  // 'hidden'/'auto') never turns this element into a scroll container, so
  // it can't change which ancestor the sticky header below sticks relative
  // to. Desktop is untouched.
  // identity + hero band — a soft gradient background and larger type (both
  // breakpoints, per a style reference the user shared) instead of the plain
  // white card + smaller type this had before. On phone the hero figures get
  // their own full-width row below the name/badges (evenly spaced, no
  // horizontal scroll) instead of being squeezed onto the same line as the
  // avatar — that scrolled sliver read as cramped next to the reference's
  // generous spacing.
  const identityBand = (
    <div style={{ display: 'flex', flexDirection: phone ? 'column' : 'row', alignItems: phone ? 'stretch' : 'center', justifyContent: 'space-between', gap: phone ? 14 : 20, flexWrap: phone ? 'nowrap' : 'wrap', minWidth: 0, background: 'linear-gradient(120deg, #ECFEFF 0%, #F0FDFA 45%, #FFFFFF 100%)', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: phone ? '14px 16px' : '18px 22px', animation: 'vjRise .4s cubic-bezier(.22,1,.36,1) both' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: phone ? 10 : 14, minWidth: 0 }}>
        <div style={{ width: phone ? 42 : 56, height: phone ? 42 : 56, borderRadius: 999, background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: phone ? 14 : 17, fontWeight: 700, color: '#2563EB', flex: phone ? '0 0 42px' : '0 0 56px' }}>{identity.initials}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '4px 10px', flexWrap: 'wrap', flexDirection: identityDir, alignItems: identityAlign }}>
            <div style={{ fontSize: phone ? 17 : 24, fontWeight: 700, letterSpacing: '-0.015em', whiteSpace: 'nowrap' }}>{identity.name}</div>
            {!phone && <div style={{ fontSize: 13.5, color: '#6B7280', whiteSpace: 'nowrap' }}>{identity.role}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: phone ? 6 : 8, marginTop: phone ? 5 : 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: phone ? '3px 8px' : '4px 10px', borderRadius: 999, background: '#ECFDF5', color: '#047857', fontSize: phone ? 10.5 : 11.5, fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: '#047857' }} />{identity.status}
            </div>
            <div style={{ padding: phone ? '3px 8px' : '4px 10px', borderRadius: 999, background: '#FFFFFF', border: '1px solid #EDEFF3', fontSize: phone ? 10.5 : 11.5, fontWeight: 600, color: '#6B7280', whiteSpace: 'nowrap' }}>{identity.scope}</div>
          </div>
        </div>
      </div>
      <div style={phone ? { display: 'flex', justifyContent: 'space-between', gap: 10 } : { display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        {heroFigures.map((f) => (
          <div key={f.label}>
            <div style={{ fontSize: phone ? 9 : 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6B7280', whiteSpace: 'nowrap' }}>{f.label}</div>
            <div style={{ fontSize: phone ? 19 : 27, fontWeight: 800, letterSpacing: '-0.02em', marginTop: phone ? 2 : 5, color: f.color, whiteSpace: 'nowrap' }}>{f.value}</div>
            <div style={{ fontSize: phone ? 10.5 : 12, color: '#9CA3AF', marginTop: 2 }}>{f.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="sdn-root" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', overflowX: phone ? 'clip' : 'visible' }}>

      {/* On phone, the title+filter row AND the identity card are NOT part of
          the sticky group — both scroll away normally as soon as you scroll
          down (they were the two biggest space costs of the sticky block).
          Only the tab strip stays pinned, for quick tab-switching. Desktop
          keeps the original structure — title bar, identity band and tab
          strip all sticky together — completely unchanged. */}
      {phone && titleBar}
      {phone && <div style={{ padding: '10px 14px 8px' }}>{identityBand}</div>}

      {/* sticky group: on phone this is just the tab strip; on desktop it's
          the title bar + identity band + tab strip together, as before */}
      <div style={{ position: 'sticky', top: 0, zIndex: 30, background: '#F7F8FA' }}>

        {!phone && titleBar}

        <div style={{ display: 'flex', flexDirection: 'column', gap: phone ? 8 : 12, width: '100%', padding: phone ? '0 14px 10px' : stickyPad }}>

          {!phone && identityBand}

          {/* sub-tab strip — a single horizontally-scrollable row on phone
              instead of wrapping across ~4 rows; desktop keeps its original
              wrapping grid untouched */}
          <div className={phone ? 'om-scroll' : undefined} style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: phone ? '8px 10px' : '10px 12px', display: 'flex', alignItems: 'center', gap: '8px 6px', flexWrap: phone ? 'nowrap' : 'wrap', overflowX: phone ? 'auto' : 'visible', minWidth: phone ? 0 : undefined }}>
            {subTabs.map((s) => (
              <React.Fragment key={s.label}>
                {s.divider && <div style={{ width: 1, alignSelf: 'stretch', background: '#EDEFF3', margin: '2px 6px', flex: '0 0 1px' }} />}
                <div
                  onClick={s.select}
                  className={s.hoverable ? 'sdn-hover-tab' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: phone ? '7px 11px' : '8px 13px', borderRadius: 9, cursor: 'pointer', whiteSpace: 'nowrap', flex: '0 0 auto', fontSize: 13, background: s.bg, color: s.fg, fontWeight: s.weight, boxShadow: s.shadow }}
                >
                  <Icon name={s.icon} size={15} color={s.iconBg} />
                  {s.label}
                  {s.count != null && <div style={{ padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: s.countBg, color: s.countFg }}>{s.count}</div>}
                </div>
              </React.Fragment>
            ))}
          </div>

        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', minWidth: 0, padding: pagePad }}>

        {/* ---------------- Overview ---------------- */}
        {/* Unlike every other tab, Overview is bespoke JSX rather than going
            through panels()'s isPlaceholder guard, so it never showed the
            same "Loading…" state on first visit that other tabs get for
            free — it just rendered its full layout with baseMetrics()'s
            zero defaults immediately. This adds the same first-load
            placeholder the other tabs already have; once overview has
            loaded once, the real layout renders (and keeps showing the
            last-loaded numbers + spinner on subsequent refreshes, same as
            every other tab). */}
        {isOverview && !overview && (
          <NotConnectedPanel title="Overview" loading={overviewLoading} />
        )}
        {isOverview && overview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            <div style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: '13px 18px', display: 'flex', alignItems: 'center', gap: '18px 26px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, paddingRight: 6 }}>
                <div style={{ position: 'relative', width: 8, height: 8, flex: '0 0 8px' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A', animation: 'vjBeat 2.2s ease-in-out infinite' }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Today</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{nowIst.date} · {nowIst.time}</div>
              </div>
              {todayStrip.map((t) => (
                <div key={t.label} style={{ display: 'flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', color: t.color }}>{t.value}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{t.sub}</div>
                </div>
              ))}
            </div>

            {/* the journey funnel */}
            <div style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '14px 16px', flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ position: 'relative', width: 9, height: 9, flex: '0 0 9px' }}>
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A', animation: 'vjBeat 2.2s ease-in-out infinite' }} />
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A', animation: 'vjHalo 2.2s ease-out infinite' }} />
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>The journey · lead → agreement</div>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 4 }}>{periodTitle} · two routes to an allotment. Conversion is measured inside a route, never across the branch.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
                  {monthFigures.map((f) => (
                    <div key={f.label}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9CA3AF' }}>{f.label}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 3 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>{f.value}</div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: f.color }}>{f.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {!phone ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* S1 · Leads */}
                <div className="sdn-hover-card" style={{ flex: '1 1 0', minWidth: 0, minHeight: 112, borderRadius: 14, padding: '11px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: s1.tint, border: `1px solid ${s1.border}`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s1.color }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={s1.icon} size={13} color={s1.color} /></div>
                    <div style={{ width: 18, height: 18, borderRadius: 999, background: s1.color, color: '#FFFFFF', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</div>
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginTop: 11 }}>{s1.label}</div>
                  <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 2, color: s1.color }}>{s1.value}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 2 }}><div style={{ fontSize: 10.5, color: '#6B7280' }}>{s1.sub}</div><div style={{ fontSize: 10.5, fontWeight: 600, color: s1.deltaColor }}>{s1.delta}</div></div>
                </div>
                {/* connector 1 -> 2 */}
                <div style={{ flex: `0 0 ${bandConnW}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0' }}>
                  <div style={{ padding: '3px 9px', borderRadius: 999, background: '#FFFFFF', border: `1px solid ${s1.connTo}`, color: s1.connTo, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{s1.pct}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', flexDirection: forkDir }}>
                    <div style={{ flex: 1, height: 3, borderRadius: 3, position: 'relative', overflow: 'hidden', background: `linear-gradient(90deg,${s1.connFrom},${s1.connTo})` }}><div style={{ position: 'absolute', top: 0, bottom: 0, width: '34%', background: 'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.95),rgba(255,255,255,0))', animation: 'vjSpark 2.2s cubic-bezier(.4,0,.6,1) infinite 0s' }} /></div>
                    <div style={{ width: 8, height: 11, background: s1.connTo, clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
                  </div>
                  <div style={{ fontSize: 9.5, color: '#9CA3AF', textAlign: 'center', whiteSpace: 'nowrap' }}>{s1.pctNote}</div>
                </div>
                {/* S2 · Site visits */}
                <div className="sdn-hover-card" style={{ flex: '1 1 0', minWidth: 0, minHeight: 112, borderRadius: 14, padding: '11px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: s2.tint, border: `1px solid ${s2.border}`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s2.color }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={s2.icon} size={13} color={s2.color} /></div>
                    <div style={{ width: 18, height: 18, borderRadius: 999, background: s2.color, color: '#FFFFFF', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</div>
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginTop: 11 }}>{s2.label}</div>
                  <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 2, color: s2.color }}>{s2.value}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 2 }}><div style={{ fontSize: 10.5, color: '#6B7280' }}>{s2.sub}</div><div style={{ fontSize: 10.5, fontWeight: 600, color: s2.deltaColor }}>{s2.delta}</div></div>
                </div>

                {/* the fork */}
                <div style={{ flex: '2.5 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
                  <div style={{ borderRadius: 14, padding: 9, background: lanes.tokenBg, border: `1px solid ${lanes.tokenBorder}`, display: 'grid', alignItems: 'center', gap: '6px 8px', gridTemplateColumns: forkCols }}>
                    <div style={{ padding: '4px 9px', borderRadius: 999, background: lanes.tokenTagBg, color: lanes.tokenTag, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', justifySelf: 'start', gridColumn: '1 / -1' }}>Event + PS</div>
                    <div className="sdn-hover-card" style={{ minHeight: 64, borderRadius: 10, padding: '8px 9px', background: '#FFFFFF', border: `1px solid ${lanes.tokenBorder}` }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>Tokens</div>
                      <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 1, color: fork.tokenColor }}>{fork.tokens}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}><div style={{ fontSize: 10.5, color: '#6B7280' }}>{fork.tokenNote}</div><div style={{ fontSize: 10.5, fontWeight: 600, color: fork.tokenDeltaColor }}>{fork.tokenDelta}</div></div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                      <div style={{ padding: '3px 8px', borderRadius: 999, background: '#FFFFFF', border: `1px solid ${fork.allotColor}`, color: fork.allotColor, fontSize: 11, fontWeight: 700 }}>{fork.tokenPct}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', flexDirection: forkDir }}>
                        <div style={{ flex: 1, height: 3, borderRadius: 3, position: 'relative', overflow: 'hidden', background: `linear-gradient(90deg,${fork.tokenColor},${fork.allotColor})` }}><div style={{ position: 'absolute', top: 0, bottom: 0, width: '34%', background: 'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.95),rgba(255,255,255,0))', animation: 'vjSpark 2.2s cubic-bezier(.4,0,.6,1) infinite .45s' }} /></div>
                        <div style={{ width: 8, height: 11, background: fork.allotColor, clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
                      </div>
                      <div style={{ fontSize: 9, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.3 }}>to allot</div>
                    </div>
                    <div style={{ minHeight: 64, borderRadius: 10, padding: '8px 9px', background: '#FFFFFF', border: '1px solid #BFDBFE' }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Token allot.</div>
                      <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 1, color: fork.allotColor }}>{fork.viaToken}</div>
                      <div style={{ fontSize: 10.5, color: '#6B7280' }}>event + preselect</div>
                    </div>
                  </div>

                  <div style={{ borderRadius: 14, padding: 9, background: lanes.sustBg, border: `1px solid ${lanes.sustBorder}`, display: 'grid', alignItems: 'center', gap: '6px 8px', gridTemplateColumns: forkCols }}>
                    <div style={{ padding: '4px 9px', borderRadius: 999, background: lanes.sustTagBg, color: lanes.sustTag, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', justifySelf: 'start', gridColumn: '1 / -1' }}>Sustenance</div>
                    <div style={{ gridColumn: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                      <div style={{ padding: '3px 8px', borderRadius: 999, background: '#FFFFFF', border: `1px solid ${fork.directColor}`, color: fork.directColor, fontSize: 11, fontWeight: 700 }}>{fork.directPct}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', flexDirection: forkDir }}>
                        <div style={{ flex: 1, height: 3, borderRadius: 3, position: 'relative', overflow: 'hidden', background: `linear-gradient(90deg,#F97316,${fork.directColor})` }}><div style={{ position: 'absolute', top: 0, bottom: 0, width: '34%', background: 'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.95),rgba(255,255,255,0))', animation: 'vjSpark 2.2s cubic-bezier(.4,0,.6,1) infinite .9s' }} /></div>
                        <div style={{ width: 8, height: 11, background: fork.directColor, clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
                      </div>
                    </div>
                    <div style={{ minHeight: 64, borderRadius: 10, padding: '8px 9px', background: '#FFFFFF', border: `1px solid ${lanes.sustBorder}` }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Direct allot.</div>
                      <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 1, color: fork.directColor }}>{fork.direct}</div>
                      <div style={{ fontSize: 10.5, color: '#6B7280' }}>no token</div>
                    </div>
                    <div style={{ gridColumn: '1 / -1', fontSize: 9, color: '#9CA3AF', paddingTop: 1 }}>Straight from the visit — this route has no token stage</div>
                  </div>
                </div>

                {/* arrow into allotments */}
                <div style={{ flex: '0 0 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ flex: 1, height: 3, borderRadius: 3, background: '#2563EB', opacity: 0.35 }} />
                  <div style={{ width: 8, height: 11, background: '#2563EB', clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
                </div>
                {/* Allotments · total */}
                <div className="sdn-hover-card" style={{ flex: '1 1 0', minWidth: 0, minHeight: 112, borderRadius: 14, padding: '11px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: tail.allotTint, border: `1px solid ${tail.allotBorder}`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: tail.allotColor }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={tail.allotIcon} size={13} color={tail.allotColor} /></div>
                    <div style={{ width: 18, height: 18, borderRadius: 999, background: tail.allotColor, color: '#FFFFFF', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</div>
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginTop: 11 }}>Allotments · total</div>
                  <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 2, color: tail.allotColor }}>{tail.allotments}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 2 }}><div style={{ fontSize: 10.5, color: '#6B7280' }}>{tail.allotSub}</div><div style={{ fontSize: 10.5, fontWeight: 600, color: tail.allotDeltaColor }}>{tail.allotDelta}</div></div>
                </div>
                {/* connector allot -> agmt */}
                <div style={{ flex: `0 0 ${bandConnW}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0' }}>
                  <div style={{ padding: '3px 9px', borderRadius: 999, background: '#FFFFFF', border: `1px solid ${tail.agmtColor}`, color: tail.agmtColor, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{tail.agmtPct}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', flexDirection: forkDir }}>
                    <div style={{ flex: 1, height: 3, borderRadius: 3, position: 'relative', overflow: 'hidden', background: `linear-gradient(90deg,${tail.allotColor},${tail.agmtColor})` }}><div style={{ position: 'absolute', top: 0, bottom: 0, width: '34%', background: 'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.95),rgba(255,255,255,0))', animation: 'vjSpark 2.2s cubic-bezier(.4,0,.6,1) infinite 1.35s' }} /></div>
                    <div style={{ width: 8, height: 11, background: tail.agmtColor, clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
                  </div>
                  <div style={{ fontSize: 9.5, color: '#9CA3AF', whiteSpace: 'nowrap' }}>registered</div>
                </div>
                {/* Agreements */}
                <div className="sdn-hover-card" style={{ flex: '1 1 0', minWidth: 0, minHeight: 112, borderRadius: 14, padding: '11px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: tail.agmtTint, border: `1px solid ${tail.agmtBorder}`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: tail.agmtColor }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={tail.agmtIcon} size={13} color={tail.agmtColor} /></div>
                    <div style={{ width: 18, height: 18, borderRadius: 999, background: tail.agmtColor, color: '#FFFFFF', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>5</div>
                  </div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginTop: 11 }}>Agreements</div>
                  <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginTop: 2, color: tail.agmtColor }}>{tail.agreements}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: 2 }}><div style={{ fontSize: 10.5, color: '#6B7280' }}>registered</div><div style={{ fontSize: 10.5, fontWeight: 600, color: tail.agmtDeltaColor }}>{tail.agmtDelta}</div></div>
                </div>
              </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <JourneyStageCard step={1} icon={s1.icon} label={s1.label} value={s1.value} sub={s1.sub} delta={s1.delta} deltaColor={s1.deltaColor} color={s1.color} tint={s1.tint} border={s1.border} />
                  <JourneyConnector pct={s1.pct} note={s1.pctNote} color={s1.connTo} to={s1.connTo} />
                  <JourneyStageCard step={2} icon={s2.icon} label={s2.label} value={s2.value} sub={s2.sub} delta={s2.delta} deltaColor={s2.deltaColor} color={s2.color} tint={s2.tint} border={s2.border} />
                  <JourneyConnector note="Two routes to an allotment" />

                  {/* the fork — each lane stacked as its own mini flow instead
                      of sitting side-by-side */}
                  <div style={{ borderRadius: 14, padding: 10, background: lanes.tokenBg, border: `1px solid ${lanes.tokenBorder}`, marginBottom: 10 }}>
                    <div style={{ padding: '4px 9px', borderRadius: 999, background: lanes.tokenTagBg, color: lanes.tokenTag, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'inline-block', marginBottom: 8 }}>Event + PS</div>
                    <div style={{ borderRadius: 10, padding: '9px 10px', background: '#FFFFFF', border: `1px solid ${lanes.tokenBorder}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>Tokens</div>
                      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 1, color: fork.tokenColor }}>{fork.tokens}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}><div style={{ fontSize: 11, color: '#6B7280' }}>{fork.tokenNote}</div><div style={{ fontSize: 11, fontWeight: 600, color: fork.tokenDeltaColor }}>{fork.tokenDelta}</div></div>
                    </div>
                    <JourneyConnector pct={fork.tokenPct} note="to allot" color={fork.allotColor} to={fork.allotColor} />
                    <div style={{ borderRadius: 10, padding: '9px 10px', background: '#FFFFFF', border: '1px solid #BFDBFE' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Token allot.</div>
                      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 1, color: fork.allotColor }}>{fork.viaToken}</div>
                      <div style={{ fontSize: 11, color: '#6B7280' }}>event + preselect</div>
                    </div>
                  </div>

                  <div style={{ borderRadius: 14, padding: 10, background: lanes.sustBg, border: `1px solid ${lanes.sustBorder}`, marginBottom: 10 }}>
                    <div style={{ padding: '4px 9px', borderRadius: 999, background: lanes.sustTagBg, color: lanes.sustTag, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'inline-block', marginBottom: 8 }}>Sustenance</div>
                    <JourneyConnector pct={fork.directPct} color={fork.directColor} to={fork.directColor} />
                    <div style={{ borderRadius: 10, padding: '9px 10px', background: '#FFFFFF', border: `1px solid ${lanes.sustBorder}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Direct allot.</div>
                      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 1, color: fork.directColor }}>{fork.direct}</div>
                      <div style={{ fontSize: 11, color: '#6B7280' }}>no token</div>
                    </div>
                    <div style={{ fontSize: 9.5, color: '#9CA3AF', marginTop: 8 }}>Straight from the visit — this route has no token stage</div>
                  </div>

                  <JourneyConnector color="#2563EB" />
                  <JourneyStageCard step={4} icon={tail.allotIcon} label="Allotments · total" value={tail.allotments} sub={tail.allotSub} delta={tail.allotDelta} deltaColor={tail.allotDeltaColor} color={tail.allotColor} tint={tail.allotTint} border={tail.allotBorder} />
                  <JourneyConnector pct={tail.agmtPct} note="registered" color={tail.agmtColor} to={tail.agmtColor} />
                  <JourneyStageCard step={5} icon={tail.agmtIcon} label="Agreements" value={tail.agreements} sub="registered" delta={tail.agmtDelta} deltaColor={tail.agmtDeltaColor} color={tail.agmtColor} tint={tail.agmtTint} border={tail.agmtBorder} />
                </div>
              )}

              {/* On phone these were reading as more journey stages (same
                  tinted-card language as JourneyStageCard, just narrower) —
                  a small section label plus a white card + left accent
                  (instead of the full tint background) marks this as a
                  separate "current state" summary, not more flow steps.
                  Desktop's original tinted cards are unchanged. */}
              {phone && <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9CA3AF', marginTop: 18, marginBottom: 8 }}>Where things stand now</div>}
              <div style={{ display: 'grid', gap: 12, marginTop: phone ? 0 : 16, gridTemplateColumns: gridFour }}>
                {outcomes.map((o) => (
                  <div key={o.label} style={phone
                    ? { minHeight: 74, borderRadius: 10, padding: '10px 12px', background: '#FFFFFF', border: '1px solid #EDEFF3', borderLeft: `3px solid ${o.color}` }
                    : { minHeight: 74, borderRadius: 12, padding: '10px 12px', background: o.bg, border: `1px solid ${o.border}` }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>{o.label}</div>
                    <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 4, color: o.color }}>{o.value}</div>
                    <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>{o.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* daily activity chart */}
            <div style={{ background: '#FFFFFF', border: '1px solid #EDEFF3', borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>Daily activity</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 4 }}>{reachLine}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {series.map((s) => (
                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#6B7280' }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />{s.label}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ position: 'relative', marginTop: 18 }}>
                {hovered && (
                  <div style={{ position: 'absolute', top: -6, right: 0, zIndex: 5, background: '#111827', color: '#fff', borderRadius: 10, padding: '10px 12px', fontSize: 12, minWidth: 150, boxShadow: '0 6px 20px rgba(16,24,40,.18)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{hovered.dateLabel}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#D1D5DB' }}>Calls<span style={{ color: '#fff' }}>{hovered.calls}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#D1D5DB' }}>Site visits<span style={{ color: '#fff' }}>{hovered.visits}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#D1D5DB' }}>Bookings<span style={{ color: '#fff' }}>{hovered.bookings}</span></div>
                  </div>
                )}
                {daysOut.length > 0 ? (
                  // Columns grow to fill the width when there's room (flex-grow 1,
                  // same look as before), but never shrink past an 18px basis — once
                  // that many days no longer fit, this wrapper scrolls horizontally
                  // instead of squeezing bars into unreadably thin slivers.
                  <div style={{ overflowX: 'auto', paddingBottom: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 170, borderBottom: '1px solid #F1F3F7', minWidth: '100%' }}>
                      {daysOut.map((d) => (
                        <div key={d.date} onMouseEnter={d.enter} onMouseLeave={d.leave} style={{ flex: '1 0 18px', height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 2, borderRadius: '5px 5px 0 0', background: d.colBg }}>
                          <div style={{ width: 5, borderRadius: '3px 3px 0 0', background: '#8B5CF6', height: d.hCalls }} />
                          <div style={{ width: 5, borderRadius: '3px 3px 0 0', background: '#F97316', height: d.hVisits }} />
                          <div style={{ width: 5, borderRadius: '3px 3px 0 0', background: '#2563EB', height: d.hBookings }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 5, marginTop: 7, minWidth: '100%' }}>
                      {daysOut.map((d) => <div key={d.date} style={{ flex: '1 0 18px', textAlign: 'center', fontSize: 10.5, color: d.labelColor }}>{d.dayNum}</div>)}
                    </div>
                  </div>
                ) : (
                  <div style={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13 }}>
                    {overviewLoading ? 'Loading…' : 'No activity data for this period yet.'}
                  </div>
                )}
              </div>
              {chartRead && <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid #F1F3F7', fontSize: 13.5 }}>{chartRead}</div>}
            </div>
          </div>
        )}

        {/* ---------------- everything else: Performance / Leads & Calls / Persona /
             Site Visits / Tokens / Allotments / Agreements / Projects ---------------- */}
        {!isOverview && otherPanels.map((p, i) => (
          <React.Fragment key={i}>
            {p.isPlaceholder && <NotConnectedPanel title={p.title} loading={p.loading} />}
            {p.isDonutRow && <DonutRow items={p.rowItems} />}
            {p.isNoteOnly && (
              <NotePanel title={p.title} open={p.showNoteBox} onToggle={p.toggle} showBox={p.showNoteBox} noteText={p.noteText} noteBg={p.noteBg} noteBorder={p.noteBorder} noteColor={p.noteColor} />
            )}
            {p.isCard && (
              <CardShell title={p.title} sub={p.sub}>
                {p.isStats && <StatsGrid cols={p.statCols} stats={p.stats} onStatClick={(s) => openPersonaDrilldown(s.dimension, s.dimValue, s.label)} phone={phone} />}
                {p.isStats && p.extraBookingType && (
                  <>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6B7280', marginTop: 20 }}>Booking type</div>
                    <BookingTypeBar items={p.extraBookingType} />
                  </>
                )}
                {p.isTable && <DataTable cols={p.colsOut} grid={p.grid} minWidth={p.minWidth} rows={p.rows} footer={p.footer} phone={phone} filters={p.filters} capped={p.capped} controlled={p.controlled} loading={p.loading} />}
                {p.isBars && <BarsList bars={p.bars} note={p.showBarNote ? p.note : null} onBarClick={p.dimension ? (b) => openPersonaDrilldown(p.dimension, b.dimValue, b.label) : undefined} />}
                {p.isTimingRow && <TimingRow timing={p.timing} totalBooked={p.totalBooked} />}
                {p.isMonthly && <MonthlyTrendTable monthly={p.monthly} />}
                {p.isProjectDetails && <ProjectDetailsTable rows={p.detailRows} totals={p.detailTotals} onOpen={p.onOpenProject} phone={phone} />}
              </CardShell>
            )}
          </React.Fragment>
        ))}
        {openProject && projects && (
          <ProjectDetailModal
            projectName={openProject.name}
            subtitle={'Allotments · ' + (M.name || '') + ' · ' + periodLabel}
            rows={projects.dealRows.filter((r) => r.projectId === openProject.projectId)}
            statusFilter={projectStatusFilter}
            onStatusFilter={setProjectStatusFilter}
            onClose={() => setOpenProject(null)}
          />
        )}
        {personaDrilldown && (
          <PersonaDrilldownModal
            title={personaDrilldown.title}
            data={personaDrilldownData}
            loading={personaDrilldownLoading}
            onClose={() => setPersonaDrilldown(null)}
          />
        )}
      </div>
    </div>
  );
}
